import React, { useState, useEffect } from "react";
import { FiArrowUp, FiArrowDown, FiEye, FiEyeOff, FiSave, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { MdDragIndicator, MdCategory, MdSubdirectoryArrowRight } from "react-icons/md";
import { FaCheckCircle } from "react-icons/fa";
import { BsGrid, BsCreditCard2Back, BsPalette, BsPhone } from "react-icons/bs";
import { getCategories, getCategoryCustomization, saveCategoryCustomization, getHomeTheme, saveHomeTheme } from "../services/ClientService";

const DEFAULT_CATEGORY_PROMO_LABELS = [
    'Starting @ ₹99',
    'Up to 50% Off',
    'Top Deals',
    'New Arrivals',
    'Best Sellers',
    'Shop Now',
];

const LAYOUTS_WITH_PROMO_LABELS = ['cloud-glass-grid', 'cloud-glass-4col-fixed', 'zepto-mosaic-grid'];

// =============================================
// 30 CATEGORY LAYOUT TYPE OPTIONS
// =============================================
const LAYOUT_OPTIONS = [
    { value: 'circle-scroll', label: 'Circle Scroll', icon: '⭕', desc: 'Classic circular icons in a horizontal scroll' },
    { value: 'grid-2col', label: 'Grid 2 Column', icon: '▦', desc: 'Two-column grid with rounded image cards' },
    { value: 'grid-3col', label: 'Grid 3 Column', icon: '▤', desc: 'Compact three-column grid layout' },
    { value: 'card-scroll', label: 'Card Scroll', icon: '🃏', desc: 'Square cards with overlay text, horizontal scroll' },
    { value: 'pill-tags', label: 'Pill Tags', icon: '💊', desc: 'Pill-shaped tag buttons with mini icons' },
    { value: 'banner-list', label: 'Banner List', icon: '📐', desc: 'Full-width banner cards with gradient overlays' },
    { value: 'icon-grid', label: 'Icon Grid', icon: '📱', desc: 'App-style icon tiles in a 4-column grid' },
    { value: 'masonry', label: 'Masonry Grid', icon: '🧱', desc: 'Pinterest-style staggered grid' },
    { value: 'carousel-featured', label: 'Featured Carousel', icon: '🎠', desc: 'Large swipeable featured cards' },
    { value: 'bubble-float', label: 'Bubble Float', icon: '🫧', desc: 'Floating bubble circles with varied sizes' },
    { value: 'diamond-grid', label: 'Diamond Grid', icon: '💎', desc: 'Rotated 45-degree diamond-shaped image tiles' },
    { value: 'hexagon-grid', label: 'Hexagon Grid', icon: '⬡', desc: 'Honeycomb hexagonal pattern tiles' },
    { value: 'wave-scroll', label: 'Wave Scroll', icon: '🌊', desc: 'Sine-wave positioned cards horizontal scroll' },
    { value: 'polaroid-scroll', label: 'Polaroid Scroll', icon: '📸', desc: 'Tilted polaroid photo frames horizontal scroll' },
    { value: 'glass-cards', label: 'Glass Cards', icon: '🪟', desc: 'Frosted glassmorphism translucent cards' },
    { value: 'neon-pills', label: 'Neon Pills', icon: '💡', desc: 'Dark-theme neon-bordered pill chips' },
    { value: 'story-circles', label: 'Story Circles', icon: '📱', desc: 'Instagram-style gradient-bordered story rings' },
    { value: 'ribbon-cards', label: 'Ribbon Cards', icon: '🎀', desc: 'Cards with corner ribbon badge accents' },
    { value: 'spotlight-duo', label: 'Spotlight Duo', icon: '🔦', desc: '1 large plus 2 small side-by-side layout' },
    { value: 'film-strip', label: 'Film Strip', icon: '🎞️', desc: 'Cinema film-strip style horizontal scroll' },
    { value: 'minimal-text', label: 'Minimal Text', icon: '✏️', desc: 'Text-only clean typography list' },
    { value: 'overlap-scroll', label: 'Overlap Scroll', icon: '🔗', desc: 'Overlapping cards in a horizontal scroll' },
    { value: 'tag-cloud', label: 'Tag Cloud', icon: '☁️', desc: 'Varied-size tag cloud flow layout' },
    { value: 'metro-tiles', label: 'Metro Tiles', icon: '🟦', desc: 'Windows Metro-style mixed-size tiles' },
    { value: 'zigzag-list', label: 'Zigzag List', icon: '⚡', desc: 'Alternating left-right staggered cards' },
    { value: 'chip-scroll', label: 'Chip Scroll', icon: '🏷️', desc: 'Material Design chip buttons horizontal scroll' },
    { value: 'accordion-list', label: 'Accordion List', icon: '📋', desc: 'Expandable accordion-style category list' },
    { value: 'flip-cards', label: 'Flip Cards', icon: '🔄', desc: 'Interactive flip-reveal card scroll' },
    { value: 'gradient-wave', label: 'Gradient Wave', icon: '🎨', desc: 'Gradient color wave background cards' },
    { value: 'split-banner', label: 'Split Banner', icon: '◧', desc: 'Two categories split side-by-side as banners' },
    { value: 'carousel-stack', label: 'Carousel Stack', icon: '📚', desc: 'Stacked overlapping depth cards with rotation' },
    { value: 'carousel-tinder', label: 'Carousel Tinder', icon: '🔥', desc: 'Tinder-style card stack with peek behind' },
    { value: 'carousel-3d', label: 'Carousel 3D', icon: '🎲', desc: 'Perspective depth cards with 3D rotation' },
    { value: 'carousel-coverflow', label: 'Carousel CoverFlow', icon: '💿', desc: 'Center-large CoverFlow album style' },
    { value: 'carousel-minimap', label: 'Carousel Minimap', icon: '🗺️', desc: 'Paging carousel with mini thumbnail strip' },
    { value: 'carousel-parallax', label: 'Carousel Parallax', icon: '🏞️', desc: 'Wide parallax cards with oversized images' },
    { value: 'carousel-zoom', label: 'Carousel Zoom', icon: '🔍', desc: 'Zoom-in effect cards with VIEW badge' },
    { value: 'carousel-book', label: 'Carousel Book', icon: '📖', desc: 'Book page style cards with text below' },
    { value: 'carousel-timeline', label: 'Carousel Timeline', icon: '⏳', desc: 'Timeline-connected cards with dot connectors' },
    { value: 'carousel-panoramic', label: 'Carousel Panoramic', icon: '🔭', desc: 'Extra-wide panoramic cards with arrows' },
    { value: 'rounded-box-grid', label: 'Rounded Box Grid', icon: '📦', desc: 'Zepto-style grey rounded boxes in a clean 4-column grid with labels below' },
    { value: 'isometric-store-grid', label: 'Isometric Store Tiles', icon: '🏪', desc: '3D isometric platform tiles with pastel colors — Shop by Store style' },
    { value: 'grouped-pastel-grid', label: 'Grouped Pastel Grid', icon: '🗂️', desc: 'Section headers with soft pastel 4-column category tiles — Blinkit Minutes style' },
    { value: 'quick-minutes-grid', label: 'Quick Minutes Grid', icon: '⚡', desc: 'Clean white card with pastel rounded squares in a tight 4-column grid' },
    { value: 'zepto-mosaic-grid', label: 'Zepto Mosaic Grid', icon: '🧩', desc: 'Mixed-width mosaic tiles with promo banners — beauty / deals mosaic style' },
    { value: 'cloud-glass-grid', label: 'Cloud Glass Grid', icon: '☁️', desc: 'Soft cloud gradient backdrop with glassmorphism category cards' },
    { value: 'cloud-glass-4col-fixed', label: 'Cloud Glass 4-Column', icon: '🧊', desc: 'Full-width cloud glass layout — always 4 equal tiles per row on any screen' },
    { value: 'crisp-border-4col', label: 'Crisp Border 4-Col', icon: '⬜', desc: 'Clean white tiles with crisp borders — strict 4-column full-width grid' },
    { value: 'soft-shadow-4col', label: 'Soft Shadow 4-Col', icon: '🌤️', desc: 'Elevated white cards with soft shadows in a 4-column grid' },
    { value: 'gradient-chip-4col', label: 'Gradient Chip 4-Col', icon: '🎨', desc: 'Pastel gradient chip tiles in a balanced 4-column layout' },
    { value: 'ink-label-4col', label: 'Ink Label 4-Col', icon: '⬛', desc: 'Bold dark label bar beneath each category image — 4 per row' },
    { value: 'halo-circle-4col', label: 'Halo Circle 4-Col', icon: '💫', desc: 'Circular category images with accent halo ring — 4-column grid' },
    { value: 'premium-overlay-3col', label: 'Premium Overlay 3-Col', icon: '✨', desc: 'Large image tiles with elegant gradient name overlay — 3 per row' },
    { value: 'lifted-card-3col', label: 'Lifted Card 3-Col', icon: '🃏', desc: 'Floating elevated cards with image top and label bottom — 3-column' },
    { value: 'pastel-block-3col', label: 'Pastel Block 3-Col', icon: '🌸', desc: 'Bold pastel blocks with centered images — spacious 3-column grid' },
    { value: 'boutique-frame-3col', label: 'Boutique Frame 3-Col', icon: '🖼️', desc: 'Warm framed boutique-style tiles with accent border — 3 per row' },
    { value: 'boutique-frame-4col', label: 'Boutique Frame 4-Col', icon: '🖼️', desc: 'Warm framed boutique-style tiles with accent border — strict 4 per row full-width' },
    { value: 'slate-minimal-3col', label: 'Slate Minimal 3-Col', icon: '🪨', desc: 'Minimal slate-toned frames with clean typography — 3-column grid' },
];

// =============================================
// 10 SUB-CATEGORY LAYOUT OPTIONS (all horizontal scroll)
// =============================================
const SUB_LAYOUT_OPTIONS = [
    { value: 'sub-pill-scroll', label: 'Pill Scroll', icon: '💊', desc: 'Horizontal scrolling pill-shaped buttons' },
    { value: 'sub-chip-scroll', label: 'Chip Scroll', icon: '🏷️', desc: 'Material chip tags with images' },
    { value: 'sub-circle-scroll', label: 'Circle Scroll', icon: '⭕', desc: 'Small circular icons horizontal scroll' },
    { value: 'sub-card-scroll', label: 'Card Scroll', icon: '🃏', desc: 'Mini cards with image and text overlay' },
    { value: 'sub-tag-scroll', label: 'Tag Scroll', icon: '🔖', desc: 'Color-tinted tag badges horizontal scroll' },
    { value: 'sub-bubble-scroll', label: 'Bubble Scroll', icon: '🫧', desc: 'Tiny floating bubble icons' },
    { value: 'sub-glass-scroll', label: 'Glass Scroll', icon: '🪟', desc: 'Frosted glass mini cards' },
    { value: 'sub-neon-scroll', label: 'Neon Scroll', icon: '💡', desc: 'Neon-outlined text pills' },
    { value: 'sub-gradient-scroll', label: 'Gradient Scroll', icon: '🎨', desc: 'Gradient-filled rounded cards' },
    { value: 'sub-underline-scroll', label: 'Underline Scroll', icon: '✏️', desc: 'Clean text tabs with underline accent' },
    { value: 'sub-diamond-scroll', label: 'Diamond Scroll', icon: '💎', desc: 'Small diamond-shaped category chips' },
    { value: 'sub-outline-scroll', label: 'Outline Scroll', icon: '⬜', desc: 'Outlined text pill buttons' },
    { value: 'sub-shadow-scroll', label: 'Shadow Scroll', icon: '🌗', desc: 'Elevated shadow-lifted pills' },
    { value: 'sub-badge-scroll', label: 'Badge Scroll', icon: '🔴', desc: 'Notification badge style tokens' },
    { value: 'sub-ribbon-scroll', label: 'Ribbon Scroll', icon: '🎀', desc: 'Ribbon tabs with left border accent' },
    { value: 'sub-dot-scroll', label: 'Dot Scroll', icon: '⚪', desc: 'Text with dot indicator below selected' },
    { value: 'sub-square-scroll', label: 'Square Scroll', icon: '🟪', desc: 'Rounded square thumbnails' },
    { value: 'sub-icon-label-scroll', label: 'Icon Label Scroll', icon: '🏷️', desc: 'Icon + label chips teal themed' },
    { value: 'sub-wave-scroll', label: 'Wave Scroll', icon: '🌊', desc: 'Sine wave offset positioned' },
    { value: 'sub-pastel-scroll', label: 'Pastel Scroll', icon: '🎨', desc: 'Pastel color array backgrounds' },
    { value: 'sub-metro-scroll', label: 'Metro Scroll', icon: '🟦', desc: 'Metro color left-border tabs' },
    { value: 'sub-minimal-scroll', label: 'Minimal Scroll', icon: '✏️', desc: 'Text-only with bold selected' },
    { value: 'sub-rounded-scroll', label: 'Rounded Scroll', icon: '💊', desc: 'Super capsule indigo accent' },
    { value: 'sub-bold-scroll', label: 'Bold Scroll', icon: '🅱️', desc: 'Heavy weight + underline accent' },
    { value: 'sub-accent-scroll', label: 'Accent Scroll', icon: '🟧', desc: 'Orange left-border accent tabs' },
    { value: 'sub-float-scroll', label: 'Float Scroll', icon: '🎈', desc: 'Floating shadow effect pills' },
    { value: 'sub-story-scroll', label: 'Story Scroll', icon: '📱', desc: 'Instagram gradient ring circles' },
    { value: 'sub-modern-scroll', label: 'Modern Scroll', icon: '✓', desc: 'Flat design with checkmark icon' },
    { value: 'sub-elegant-scroll', label: 'Elegant Scroll', icon: '👑', desc: 'Stone/warm tones elegant feel' },
    { value: 'sub-vibrant-scroll', label: 'Vibrant Scroll', icon: '🌈', desc: 'Per-item color accent vibrant' },
    { value: 'sub-slate-scroll', label: 'Slate Scroll', icon: '🪨', desc: 'Slate grey themed tabs' },
    { value: 'sub-aurora-scroll', label: 'Aurora Scroll', icon: '🌌', desc: 'Aurora gradient filled pills' },
    { value: 'sub-neumorphic-scroll', label: 'Neumorphic Scroll', icon: '🔘', desc: 'Neumorphic raised/pressed effect' },
    { value: 'sub-frosted-scroll', label: 'Frosted Scroll', icon: '❄️', desc: 'Frosted glass + thumbnail chips' },
    { value: 'sub-tag-label-scroll', label: 'Tag Label Scroll', icon: '🏷️', desc: 'Tag icon + label design' },
    { value: 'sub-stack-scroll', label: 'Stack Scroll', icon: '📚', desc: 'Purple stack with bottom accent' },
    { value: 'sub-capsule-scroll', label: 'Capsule Scroll', icon: '💊', desc: 'Emerald capsule buttons' },
    { value: 'sub-polaroid-scroll', label: 'Polaroid Scroll', icon: '📸', desc: 'Mini polaroid card style' },
    { value: 'sub-split-scroll', label: 'Split Scroll', icon: '◧', desc: 'Two-tone left color bar' },
    { value: 'sub-glow-scroll', label: 'Glow Scroll', icon: '💡', desc: 'Neon cyan glow on dark' },
];

// =============================================
// 8 PRODUCT CARD STYLE OPTIONS
// =============================================
const CARD_STYLE_OPTIONS = [
    { value: 'classic-grid', label: 'Classic Grid', icon: '🏪', desc: 'Original 2-column grid with rounded corners and shadow' },
    { value: 'modern-elevated', label: 'Modern Elevated', icon: '🏢', desc: 'Strong shadow, bold typography, elevated feel' },
    { value: 'minimal-clean', label: 'Minimal Clean', icon: '✨', desc: 'Borderless, soft shadow, maximum whitespace' },
    { value: 'compact-slim', label: 'Compact Slim', icon: '📏', desc: 'Smaller image, slim card, space-efficient' },
    { value: 'bold-vibrant', label: 'Bold Vibrant', icon: '🎯', desc: 'Colorful accents, indigo bottom border, vibrant pricing' },
    { value: 'elegant-luxe', label: 'Elegant Luxe', icon: '👑', desc: 'Warm tones, gradient overlay, luxurious feel' },
    { value: 'rounded-soft', label: 'Rounded Soft', icon: '🧊', desc: 'Extra rounded, pastel sky tones, friendly design' },
    { value: 'dark-premium', label: 'Dark Premium', icon: '🌑', desc: 'Dark cards, light text, premium night-mode aesthetic' },
    { value: 'glass-morphism', label: 'Glass Morphism', icon: '🪟', desc: 'Frosted glass translucent card effect' },
    { value: 'neo-brutalist', label: 'Neo Brutalist', icon: '🔲', desc: 'Heavy borders, stark shadows, raw design' },
    { value: 'gradient-border', label: 'Gradient Border', icon: '🌈', desc: 'Rainbow gradient border wrapping' },
    { value: 'floating-shadow', label: 'Floating Shadow', icon: '☁️', desc: 'Deep floating shadow elevated card' },
    { value: 'magazine-style', label: 'Magazine Style', icon: '📰', desc: 'Editorial magazine layout feel' },
    { value: 'instagram-card', label: 'Instagram Card', icon: '📸', desc: 'Instagram post-like card design' },
    { value: 'neumorphic-light', label: 'Neumorphic Light', icon: '🔘', desc: 'Soft neumorphic raised card' },
    { value: 'retro-vintage', label: 'Retro Vintage', icon: '📻', desc: 'Classic vintage warm aesthetics' },
    { value: 'neon-glow', label: 'Neon Glow', icon: '💡', desc: 'Neon glowing card on dark background' },
    { value: 'pastel-dream', label: 'Pastel Dream', icon: '🌸', desc: 'Soft pastel pink dreamy design' },
    { value: 'monochrome-clean', label: 'Monochrome Clean', icon: '⬛', desc: 'Black and white clean design' },
    { value: 'luxury-gold', label: 'Luxury Gold', icon: '🏆', desc: 'Gold accented dark luxury feel' },
    { value: 'nature-organic', label: 'Nature Organic', icon: '🌿', desc: 'Green organic nature theme' },
    { value: 'sunset-warm', label: 'Sunset Warm', icon: '🌅', desc: 'Warm orange sunset tones' },
    { value: 'arctic-cool', label: 'Arctic Cool', icon: '🧊', desc: 'Cool arctic sky blue theme' },
    { value: 'cherry-blossom', label: 'Cherry Blossom', icon: '🌸', desc: 'Rose pink blossom spring theme' },
    { value: 'midnight-royal', label: 'Midnight Royal', icon: '🔮', desc: 'Deep indigo midnight purple' },
    { value: 'carbon-fiber', label: 'Carbon Fiber', icon: '⚙️', desc: 'Dark carbon fiber industrial look' },
    { value: 'frosted-mint', label: 'Frosted Mint', icon: '🍃', desc: 'Fresh mint green frosted design' },
    { value: 'coral-reef', label: 'Coral Reef', icon: '🐠', desc: 'Coral orange with warm accent' },
    { value: 'lavender-mist', label: 'Lavender Mist', icon: '💜', desc: 'Soft lavender purple mist theme' },
    { value: 'woodland-earth', label: 'Woodland Earth', icon: '🌰', desc: 'Earthy brown woodland tones' },
    { value: 'steel-industrial', label: 'Steel Industrial', icon: '🔩', desc: 'Industrial steel grey theme' },
    { value: 'candy-pop', label: 'Candy Pop', icon: '🍬', desc: 'Playful candy pop bright colors' },
    { value: 'ocean-depth', label: 'Ocean Depth', icon: '🌊', desc: 'Deep ocean blue dark theme' },
    { value: 'rose-gold', label: 'Rose Gold', icon: '🥇', desc: 'Rose gold elegant dark theme' },
    { value: 'emerald-city', label: 'Emerald City', icon: '💚', desc: 'Deep emerald green dark theme' },
    { value: 'watercolor-soft', label: 'Watercolor Soft', icon: '🎨', desc: 'Artistic watercolor soft feel' },
    { value: 'blueprint-tech', label: 'Blueprint Tech', icon: '📐', desc: 'Tech blueprint dark blue design' },
    { value: 'split-layout', label: 'Split Layout', icon: '◧', desc: 'Clean split layout minimal card' },
];

// =============================================
// 12 NAVIGATION BAR STYLE OPTIONS
// =============================================
const NAVBAR_STYLE_OPTIONS = [
    { value: 'floating-pill', label: 'Floating Pill', icon: '💊', desc: 'Modern floating pill-shaped bottom bar with elevated shadow and rounded corners', preview: { bg: '#ffffff', radius: 28, shadow: true, iconStyle: 'gradient-circle', labelBelow: true, accent: '#6366f1' } },
    { value: 'glass-dock', label: 'Glass Dock', icon: '🪟', desc: 'Frosted glass translucent dock with blur backdrop and subtle border', preview: { bg: 'rgba(255,255,255,0.75)', radius: 24, shadow: true, iconStyle: 'glass-circle', labelBelow: true, accent: '#0ea5e9' } },
    { value: 'minimal-flat', label: 'Minimal Flat', icon: '✨', desc: 'Clean flat bottom bar with no elevation, thin top border, focused on simplicity', preview: { bg: '#ffffff', radius: 0, shadow: false, iconStyle: 'plain', labelBelow: true, accent: '#111827' } },
    { value: 'curved-notch', label: 'Curved Notch', icon: '🌙', desc: 'Beautiful curved notch cutout for center tab with smooth arc design', preview: { bg: '#ffffff', radius: 0, shadow: true, iconStyle: 'notch-center', labelBelow: true, accent: '#7c3aed' } },
    { value: 'neon-glow', label: 'Neon Glow', icon: '💡', desc: 'Dark background with neon-colored glowing active tab and electric accents', preview: { bg: '#0f172a', radius: 20, shadow: true, iconStyle: 'neon-circle', labelBelow: true, accent: '#22d3ee' } },
    { value: 'bubble-pop', label: 'Bubble Pop', icon: '🫧', desc: 'Active tab expands into a colored pill bubble with icon and label side by side', preview: { bg: '#ffffff', radius: 28, shadow: true, iconStyle: 'bubble-expand', labelBelow: false, accent: '#f43f5e' } },
    { value: 'elegant-line', label: 'Elegant Line', icon: '〰️', desc: 'Thin active indicator line above the selected tab icon with refined aesthetics', preview: { bg: '#ffffff', radius: 0, shadow: false, iconStyle: 'top-line', labelBelow: true, accent: '#b45309' } },
    { value: 'island-float', label: 'Island Float', icon: '🏝️', desc: 'Separated floating island for each tab with individual card-like containers', preview: { bg: '#f1f5f9', radius: 16, shadow: true, iconStyle: 'island-cards', labelBelow: true, accent: '#059669' } },
    { value: 'neumorphic-soft', label: 'Neumorphic Soft', icon: '🔘', desc: 'Soft neumorphic pressed/raised effect with subtle inner and outer shadows', preview: { bg: '#e5e7eb', radius: 24, shadow: false, iconStyle: 'neumorphic', labelBelow: true, accent: '#6366f1' } },
    { value: 'gradient-wave', label: 'Gradient Wave', icon: '🌊', desc: 'Full-width gradient background bar with wave-shaped top edge decoration', preview: { bg: 'linear-gradient(135deg, #6366f1, #8b5cf6)', radius: 0, shadow: true, iconStyle: 'white-icons', labelBelow: true, accent: '#ffffff' } },
    { value: 'material-you', label: 'Material You', icon: '🎨', desc: 'Google Material You inspired with active indicator pill behind icon', preview: { bg: '#ffffff', radius: 0, shadow: false, iconStyle: 'material-pill', labelBelow: true, accent: '#6366f1' } },
    { value: 'retro-arcade', label: 'Retro Arcade', icon: '🕹️', desc: 'Retro pixel-art inspired with blocky shapes, bold borders and 8-bit charm', preview: { bg: '#1a1a2e', radius: 4, shadow: true, iconStyle: 'pixel-box', labelBelow: true, accent: '#fbbf24' } },
];

// =============================================
// 32 HOME THEME OPTIONS (Festivals, Seasons, Events)
// =============================================
const HOME_THEME_OPTIONS = [
    { value: 'none', label: 'No Theme', icon: '⚪', desc: 'Default clean look – no seasonal theme applied', category: 'Default', gradient: 'from-gray-100 to-gray-200', preview: ['#f3f4f6', '#e5e7eb'] },
    { value: 'christmas', label: 'Christmas', icon: '🎄', desc: 'Snowflakes, red & green, cozy winter magic with falling snow animation', category: 'Winter', gradient: 'from-red-600 to-green-700', preview: ['#dc2626', '#15803d'] },
    { value: 'new-year', label: 'New Year', icon: '🎆', desc: 'Fireworks, gold sparkles and midnight blue celebration', category: 'Winter', gradient: 'from-yellow-500 to-indigo-900', preview: ['#eab308', '#312e81'] },
    { value: 'winter-wonderland', label: 'Winter Wonderland', icon: '❄️', desc: 'Icy blues, frost crystals and gentle snowfall', category: 'Winter', gradient: 'from-blue-200 to-blue-500', preview: ['#bfdbfe', '#3b82f6'] },
    { value: 'pongal', label: 'Pongal', icon: '🌾', desc: 'Kolam patterns, harvest gold, turmeric & sugarcane hues', category: 'Festival', gradient: 'from-yellow-400 to-orange-500', preview: ['#facc15', '#f97316'] },
    { value: 'makar-sankranti', label: 'Makar Sankranti', icon: '🪁', desc: 'Kites, bright skies, festive multi-color patola patterns', category: 'Festival', gradient: 'from-sky-400 to-orange-400', preview: ['#38bdf8', '#fb923c'] },
    { value: 'republic-day', label: 'Republic Day', icon: '🇮🇳', desc: 'Tricolor pride, saffron-white-green patriotic theme', category: 'National', gradient: 'from-orange-500 via-white to-green-600', preview: ['#f97316', '#16a34a'] },
    { value: 'valentines', label: "Valentine's Day", icon: '💕', desc: 'Floating hearts, rose pink and romantic red accents', category: 'Celebration', gradient: 'from-pink-400 to-red-500', preview: ['#f472b6', '#ef4444'] },
    { value: 'holi', label: 'Holi', icon: '🎨', desc: 'Splash of colors, vibrant gulal particles flying everywhere', category: 'Festival', gradient: 'from-pink-500 via-yellow-400 to-blue-500', preview: ['#ec4899', '#3b82f6'] },
    { value: 'spring-blossom', label: 'Spring Blossom', icon: '🌸', desc: 'Cherry blossoms, soft pinks, gentle petal rain animation', category: 'Season', gradient: 'from-pink-200 to-pink-400', preview: ['#fbcfe8', '#f472b6'] },
    { value: 'easter', label: 'Easter', icon: '🐣', desc: 'Pastel eggs, bunny motifs, fresh spring meadow greens', category: 'Celebration', gradient: 'from-purple-300 to-yellow-200', preview: ['#d8b4fe', '#fef08a'] },
    { value: 'ramadan', label: 'Ramadan', icon: '🌙', desc: 'Crescent moon, deep midnight blue, starlit gold lanterns', category: 'Festival', gradient: 'from-indigo-800 to-yellow-600', preview: ['#3730a3', '#ca8a04'] },
    { value: 'ugadi', label: 'Ugadi / Gudi Padwa', icon: '🏮', desc: 'Mango leaf toran, fresh green and gold auspicious tones', category: 'Festival', gradient: 'from-green-500 to-yellow-400', preview: ['#22c55e', '#facc15'] },
    { value: 'baisakhi', label: 'Baisakhi', icon: '🌾', desc: 'Golden wheat fields, bhangra energy, harvest celebration', category: 'Festival', gradient: 'from-amber-400 to-green-500', preview: ['#fbbf24', '#22c55e'] },
    { value: 'summer-vibes', label: 'Summer Vibes', icon: '☀️', desc: 'Tropical sunshine, bright yellows, beach wave animations', category: 'Season', gradient: 'from-yellow-300 to-orange-400', preview: ['#fde047', '#fb923c'] },
    { value: 'summer-tropical', label: 'Tropical Paradise', icon: '🌴', desc: 'Palm trees, teal ocean, coconut and flamingo accents', category: 'Season', gradient: 'from-teal-400 to-cyan-300', preview: ['#2dd4bf', '#67e8f9'] },
    { value: 'monsoon', label: 'Monsoon Rain', icon: '🌧️', desc: 'Gentle raindrops, cloudy grey-blue, puddle splash effects', category: 'Season', gradient: 'from-slate-400 to-blue-400', preview: ['#94a3b8', '#60a5fa'] },
    { value: 'independence-day', label: 'Independence Day', icon: '🇮🇳', desc: 'Tricolor fireworks, patriotic saffron-white-green sparkles', category: 'National', gradient: 'from-orange-500 via-white to-green-600', preview: ['#f97316', '#16a34a'] },
    { value: 'raksha-bandhan', label: 'Raksha Bandhan', icon: '🎀', desc: 'Sacred thread, purple & gold, sibling bond celebration', category: 'Festival', gradient: 'from-purple-500 to-amber-400', preview: ['#a855f7', '#fbbf24'] },
    { value: 'janmashtami', label: 'Janmashtami', icon: '🦚', desc: 'Peacock feather blues, butter yellows, Krishna motifs', category: 'Festival', gradient: 'from-blue-600 to-yellow-400', preview: ['#2563eb', '#facc15'] },
    { value: 'ganesh-chaturthi', label: 'Ganesh Chaturthi', icon: '🐘', desc: 'Vermillion, modak motifs, auspicious orange & red', category: 'Festival', gradient: 'from-orange-500 to-red-500', preview: ['#f97316', '#ef4444'] },
    { value: 'onam', label: 'Onam', icon: '🛶', desc: 'Pookalam flower carpet, boat race gold & green Kerala', category: 'Festival', gradient: 'from-yellow-400 to-green-600', preview: ['#facc15', '#16a34a'] },
    { value: 'navratri', label: 'Navratri / Durga Puja', icon: '🪔', desc: 'Nine nights, dandiya sticks, vibrant garba reds & golds', category: 'Festival', gradient: 'from-red-500 to-yellow-500', preview: ['#ef4444', '#eab308'] },
    { value: 'dussehra', label: 'Dussehra', icon: '🏹', desc: 'Victory of good, blazing arrow golds and deep reds', category: 'Festival', gradient: 'from-amber-500 to-red-600', preview: ['#f59e0b', '#dc2626'] },
    { value: 'halloween', label: 'Halloween', icon: '🎃', desc: 'Spooky pumpkins, bats, haunted orange-black with eerie fog', category: 'Celebration', gradient: 'from-orange-500 to-gray-900', preview: ['#f97316', '#111827'] },
    { value: 'diwali', label: 'Diwali', icon: '🪔', desc: 'Festival of lights, floating diyas, sparklers & golden glow', category: 'Festival', gradient: 'from-amber-400 to-orange-600', preview: ['#fbbf24', '#ea580c'] },
    { value: 'autumn-harvest', label: 'Autumn Harvest', icon: '🍂', desc: 'Falling leaves, warm amber & rustic brown tones', category: 'Season', gradient: 'from-amber-600 to-orange-800', preview: ['#d97706', '#9a3412'] },
    { value: 'thanksgiving', label: 'Thanksgiving', icon: '🦃', desc: 'Harvest table, warm browns, cornucopia golden orange', category: 'Celebration', gradient: 'from-orange-600 to-amber-800', preview: ['#ea580c', '#92400e'] },
    { value: 'eid', label: 'Eid Mubarak', icon: '🕌', desc: 'Mosque silhouettes, emerald green, crescent moon gold', category: 'Festival', gradient: 'from-emerald-600 to-yellow-500', preview: ['#059669', '#eab308'] },
    { value: 'lohri', label: 'Lohri', icon: '🔥', desc: 'Bonfire flames, warm orange glow, rewri & popcorn accents', category: 'Festival', gradient: 'from-orange-600 to-red-700', preview: ['#ea580c', '#b91c1c'] },
    { value: 'cherry-festival', label: 'Sakura / Cherry Festival', icon: '🌸', desc: 'Japanese cherry blossom, pale pink petals falling softly', category: 'Celebration', gradient: 'from-pink-300 to-rose-400', preview: ['#f9a8d4', '#fb7185'] },
    { value: 'mid-autumn', label: 'Mid-Autumn Moon', icon: '🌕', desc: 'Moonlit lanterns, warm amber glow, floating moonlight', category: 'Festival', gradient: 'from-yellow-300 to-amber-600', preview: ['#fde047', '#d97706'] },
    { value: 'aurora-borealis', label: 'Aurora Borealis', icon: '🌌', desc: 'Mesmerizing northern lights, deep violet to cyan shimmer across the polar sky', category: 'Season', gradient: 'from-cyan-500 to-violet-600', preview: ['#06b6d4', '#8b5cf6'] },
    { value: 'ocean-breeze', label: 'Ocean Breeze', icon: '🌊', desc: 'Calm ocean waves, crystal blue bubbles floating gently on a seaside morning', category: 'Season', gradient: 'from-sky-500 to-teal-400', preview: ['#0ea5e9', '#2dd4bf'] },
    { value: 'golden-jubilee', label: 'Golden Jubilee', icon: '🏆', desc: 'Luxurious gold celebration, sparkling stars on deep amber-black royal backdrop', category: 'Celebration', gradient: 'from-amber-500 to-amber-900', preview: ['#f59e0b', '#78350f'] },
    { value: 'neon-nights', label: 'Neon Nights', icon: '💜', desc: 'Electrifying neon purple and pink, futuristic cyberpunk city glow effects', category: 'Celebration', gradient: 'from-fuchsia-500 to-pink-500', preview: ['#d946ef', '#ec4899'] },
    { value: 'rustic-farmhouse', label: 'Rustic Farmhouse', icon: '🏡', desc: 'Warm earthy tones, gentle breeze through golden wheat and sage herbs', category: 'Season', gradient: 'from-stone-400 to-stone-500', preview: ['#a8a29e', '#78716c'] },
    { value: 'ice-crystal', label: 'Ice Crystal', icon: '💎', desc: 'Frozen crystalline beauty, delicate snowflakes on pristine icy blue glass', category: 'Winter', gradient: 'from-blue-100 to-sky-300', preview: ['#dbeafe', '#7dd3fc'] },
    { value: 'carnival-fiesta', label: 'Carnival Fiesta', icon: '🎭', desc: 'Explosive carnival energy, multi-colored confetti rain with festive reds and golds', category: 'Celebration', gradient: 'from-red-500 to-yellow-500', preview: ['#ef4444', '#eab308'] },
    { value: 'lavender-fields', label: 'Lavender Fields', icon: '💐', desc: 'Serene lavender purple meadows, gentle petals drifting on a spring breeze', category: 'Season', gradient: 'from-purple-400 to-violet-400', preview: ['#c084fc', '#a78bfa'] },
    { value: 'midnight-galaxy', label: 'Midnight Galaxy', icon: '🌠', desc: 'Deep space cosmic wonder, twinkling stars across an infinite indigo galaxy', category: 'Celebration', gradient: 'from-indigo-900 to-purple-800', preview: ['#1e1b4b', '#6b21a8'] },
    { value: 'rose-garden', label: 'Rose Garden', icon: '🌹', desc: 'Romantic rose garden, velvety red petals falling over blush pink blooms', category: 'Season', gradient: 'from-rose-300 to-rose-400', preview: ['#fda4af', '#fb7185'] },
    { value: 'mango-summer', label: 'Mango Summer', icon: '🥭', desc: 'Juicy mango sunshine, bright golden yellow warmth with tropical sparkle', category: 'Season', gradient: 'from-amber-400 to-orange-500', preview: ['#fbbf24', '#f97316'] },
    { value: 'peacock-royal', label: 'Peacock Royal', icon: '🦚', desc: 'Majestic peacock feather blues and teals, royal sparkle on emerald dark', category: 'Festival', gradient: 'from-teal-600 to-teal-400', preview: ['#0d9488', '#2dd4bf'] },
    { value: 'cotton-candy', label: 'Cotton Candy', icon: '🍭', desc: 'Sweet pastel pink and blue, dreamy cotton candy bubbles floating softly', category: 'Celebration', gradient: 'from-pink-300 to-blue-300', preview: ['#f9a8d4', '#93c5fd'] },
    { value: 'thunder-storm', label: 'Thunder Storm', icon: '⛈️', desc: 'Dramatic dark storm clouds, heavy rain streaks with electric slate atmosphere', category: 'Season', gradient: 'from-slate-600 to-slate-800', preview: ['#475569', '#1e293b'] },
    { value: 'cherry-bloom', label: 'Cherry Bloom', icon: '🍒', desc: 'Rich crimson cherry blossoms, deep red petals cascading on rosy blush sky', category: 'Season', gradient: 'from-rose-700 to-red-500', preview: ['#be123c', '#ef4444'] },
    { value: 'jade-zen', label: 'Jade Zen', icon: '🍃', desc: 'Peaceful jade green, floating bamboo leaves on tranquil emerald waters', category: 'Season', gradient: 'from-emerald-600 to-emerald-500', preview: ['#059669', '#10b981'] },
    { value: 'sunset-glow', label: 'Sunset Glow', icon: '🌅', desc: 'Breathtaking sunset horizon, blazing orange embers sparkling into coral red sky', category: 'Season', gradient: 'from-orange-500 to-red-500', preview: ['#f97316', '#ef4444'] },
    { value: 'starry-night', label: 'Starry Night', icon: '🌃', desc: 'Van Gogh inspired deep blue night sky, glowing golden stars swirling overhead', category: 'Celebration', gradient: 'from-blue-900 to-sky-800', preview: ['#1e3a5f', '#075985'] },
    { value: 'rangoli-vibrance', label: 'Rangoli Vibrance', icon: '🎨', desc: 'Vibrant rangoli colors, multicolor gulal dots on festive rose and purple canvas', category: 'Festival', gradient: 'from-rose-600 to-violet-600', preview: ['#e11d48', '#7c3aed'] },
    { value: 'vintage-sepia', label: 'Vintage Sepia', icon: '📜', desc: 'Classic vintage warmth, soft sepia tones with gentle antique golden sparkle', category: 'Season', gradient: 'from-stone-300 to-stone-400', preview: ['#d6d3d1', '#a8a29e'] },
];

const HOME_THEME_CATEGORIES = ['Default', 'Festival', 'Season', 'Celebration', 'National', 'Winter'];

// =============================================
// DUMMY DATA FOR MINI CARD PREVIEWS
// =============================================
const DUMMY_MINI_CATS = [
    { _id: 'd1', categoryName: 'Fruits', imageUrl: null, preview: null },
    { _id: 'd2', categoryName: 'Veggies', imageUrl: null, preview: null },
    { _id: 'd3', categoryName: 'Dairy', imageUrl: null, preview: null },
    { _id: 'd4', categoryName: 'Bakery', imageUrl: null, preview: null },
    { _id: 'd5', categoryName: 'Snacks', imageUrl: null, preview: null },
    { _id: 'd6', categoryName: 'Drinks', imageUrl: null, preview: null },
];

const DUMMY_MINI_SUBS = [
    { _id: 's1', categoryName: 'Apple' },
    { _id: 's2', categoryName: 'Mango' },
    { _id: 's3', categoryName: 'Banana' },
    { _id: 's4', categoryName: 'Orange' },
    { _id: 's5', categoryName: 'Grape' },
];

// =============================================
// CARD STYLE PREVIEW (Phone Mockup)
// =============================================
const CardStylePreview = ({ style }) => {
    const cards = {
        'classic-grid': {
            bg: '#fff', radius: 12, shadow: '0 2px 8px rgba(0,0,0,0.1)', imgH: 100, priceFw: 700, priceColor: '#111',
            badgeBg: '#EF4444', border: 'none', bottomBorder: 'none', nameColor: '#1f2937', weightColor: '#6b7280',
        },
        'modern-elevated': {
            bg: '#fff', radius: 16, shadow: '0 4px 16px rgba(0,0,0,0.12)', imgH: 110, priceFw: 800, priceColor: '#111',
            badgeBg: '#DC2626', border: 'none', bottomBorder: 'none', nameColor: '#111', weightColor: '#9ca3af',
        },
        'minimal-clean': {
            bg: '#fff', radius: 16, shadow: '0 2px 8px rgba(100,116,139,0.06)', imgH: 95, priceFw: 700, priceColor: '#111',
            badgeBg: 'transparent', border: 'none', bottomBorder: 'none', nameColor: '#1f2937', weightColor: '#d1d5db',
            discountInline: true, discountColor: '#059669',
        },
        'compact-slim': {
            bg: '#fff', radius: 12, shadow: '0 1px 4px rgba(0,0,0,0.04)', imgH: 80, priceFw: 700, priceColor: '#111',
            badgeBg: '#EF4444', border: '1px solid #f1f5f9', bottomBorder: 'none', nameColor: '#1f2937', weightColor: '#9ca3af',
        },
        'bold-vibrant': {
            bg: '#fff', radius: 16, shadow: '0 3px 8px rgba(99,102,241,0.1)', imgH: 100, priceFw: 800, priceColor: '#6366f1',
            badgeBg: '#f59e0b', border: 'none', bottomBorder: '3px solid #6366f1', nameColor: '#111', weightColor: '#9ca3af',
        },
        'elegant-luxe': {
            bg: '#fafaf9', radius: 20, shadow: '0 3px 10px rgba(120,113,108,0.08)', imgH: 105, priceFw: 700, priceColor: '#1c1917',
            badgeBg: '#b91c1c', border: 'none', bottomBorder: 'none', nameColor: '#1c1917', weightColor: '#a8a29e',
            hasGradientOverlay: true,
        },
        'rounded-soft': {
            bg: '#f0f9ff', radius: 24, shadow: '0 2px 8px rgba(14,165,233,0.06)', imgH: 95, priceFw: 700, priceColor: '#0c4a6e',
            badgeBg: '#f43f5e', border: '1px solid #e0f2fe', bottomBorder: 'none', nameColor: '#0c4a6e', weightColor: '#7dd3fc',
        },
        'dark-premium': {
            bg: '#1e1e2e', radius: 16, shadow: '0 4px 10px rgba(0,0,0,0.25)', imgH: 100, priceFw: 800, priceColor: '#a5f3fc',
            badgeBg: '#ef4444', border: 'none', bottomBorder: 'none', nameColor: '#fff', weightColor: '#94a3b8',
        },
        'glass-morphism': {
            bg: 'rgba(255,255,255,0.7)', radius: 20, shadow: '0 4px 16px rgba(100,116,139,0.1)', imgH: 100, priceFw: 800, priceColor: '#0f172a',
            badgeBg: 'rgba(239,68,68,0.85)', border: '1px solid rgba(255,255,255,0.4)', bottomBorder: 'none', nameColor: '#1e293b', weightColor: '#94a3b8',
        },
        'neo-brutalist': {
            bg: '#fff', radius: 4, shadow: '5px 5px 0 #000', imgH: 100, priceFw: 900, priceColor: '#000',
            badgeBg: '#fbbf24', border: '3px solid #000', bottomBorder: 'none', nameColor: '#000', weightColor: '#737373',
        },
        'gradient-border': {
            bg: '#fff', radius: 18, shadow: '0 2px 8px rgba(99,102,241,0.1)', imgH: 100, priceFw: 800, priceColor: '#6366f1',
            badgeBg: '#ec4899', border: '2px solid #6366f1', bottomBorder: 'none', nameColor: '#1e293b', weightColor: '#94a3b8',
        },
        'floating-shadow': {
            bg: '#fff', radius: 20, shadow: '0 8px 20px rgba(0,0,0,0.15)', imgH: 105, priceFw: 800, priceColor: '#111',
            badgeBg: '#ef4444', border: 'none', bottomBorder: 'none', nameColor: '#111', weightColor: '#9ca3af',
        },
        'magazine-style': {
            bg: '#fff', radius: 2, shadow: 'none', imgH: 115, priceFw: 300, priceColor: '#111',
            badgeBg: '#111', border: 'none', bottomBorder: '2px solid #111', nameColor: '#111', weightColor: '#a3a3a3',
        },
        'instagram-card': {
            bg: '#fff', radius: 16, shadow: 'none', imgH: 105, priceFw: 700, priceColor: '#262626',
            badgeBg: '#e1306c', border: '1px solid #efefef', bottomBorder: 'none', nameColor: '#262626', weightColor: '#8e8e8e',
        },
        'neumorphic-light': {
            bg: '#e5e5e5', radius: 20, shadow: '-4px -4px 6px rgba(255,255,255,0.8)', imgH: 95, priceFw: 800, priceColor: '#404040',
            badgeBg: '#ef4444', border: 'none', bottomBorder: 'none', nameColor: '#525252', weightColor: '#a3a3a3',
        },
        'retro-vintage': {
            bg: '#fefce8', radius: 6, shadow: 'none', imgH: 100, priceFw: 900, priceColor: '#854d0e',
            badgeBg: '#ca8a04', border: '2px solid #ca8a04', bottomBorder: 'none', nameColor: '#713f12', weightColor: '#a16207',
        },
        'neon-glow': {
            bg: '#0a0a0a', radius: 16, shadow: '0 0 12px rgba(34,211,238,0.4)', imgH: 100, priceFw: 800, priceColor: '#22d3ee',
            badgeBg: '#ef4444', border: '1px solid #22d3ee', bottomBorder: 'none', nameColor: '#e2e8f0', weightColor: '#64748b',
        },
        'pastel-dream': {
            bg: '#fdf2f8', radius: 22, shadow: '0 2px 8px rgba(236,72,153,0.06)', imgH: 100, priceFw: 800, priceColor: '#9d174d',
            badgeBg: '#ec4899', border: '1px solid #fbcfe8', bottomBorder: 'none', nameColor: '#831843', weightColor: '#f9a8d4',
        },
        'monochrome-clean': {
            bg: '#fff', radius: 12, shadow: 'none', imgH: 105, priceFw: 700, priceColor: '#171717',
            badgeBg: '#171717', border: '1px solid #e5e5e5', bottomBorder: '1px solid #e5e5e5', nameColor: '#171717', weightColor: '#a3a3a3',
        },
        'luxury-gold': {
            bg: '#1c1917', radius: 16, shadow: '0 3px 10px rgba(180,83,9,0.15)', imgH: 110, priceFw: 800, priceColor: '#f59e0b',
            badgeBg: '#b45309', border: '1px solid #b45309', bottomBorder: 'none', nameColor: '#fef3c7', weightColor: '#78716c',
        },
        'nature-organic': {
            bg: '#f0fdf4', radius: 18, shadow: '0 2px 8px rgba(22,163,74,0.06)', imgH: 100, priceFw: 800, priceColor: '#166534',
            badgeBg: '#16a34a', border: '1px solid #bbf7d0', bottomBorder: 'none', nameColor: '#14532d', weightColor: '#86efac',
        },
        'sunset-warm': {
            bg: '#fff7ed', radius: 18, shadow: '0 2px 10px rgba(234,88,12,0.08)', imgH: 100, priceFw: 800, priceColor: '#c2410c',
            badgeBg: '#ea580c', border: '1px solid #fed7aa', bottomBorder: 'none', nameColor: '#7c2d12', weightColor: '#fdba74',
        },
        'arctic-cool': {
            bg: '#f0f9ff', radius: 18, shadow: '0 2px 8px rgba(2,132,199,0.06)', imgH: 100, priceFw: 800, priceColor: '#0369a1',
            badgeBg: '#0284c7', border: '1px solid #bae6fd', bottomBorder: 'none', nameColor: '#0c4a6e', weightColor: '#7dd3fc',
        },
        'cherry-blossom': {
            bg: '#fff1f2', radius: 20, shadow: '0 2px 8px rgba(225,29,72,0.06)', imgH: 100, priceFw: 800, priceColor: '#be123c',
            badgeBg: '#e11d48', border: '1px solid #fecdd3', bottomBorder: 'none', nameColor: '#881337', weightColor: '#fda4af',
        },
        'midnight-royal': {
            bg: '#1e1b4b', radius: 16, shadow: '0 4px 12px rgba(67,56,202,0.2)', imgH: 105, priceFw: 800, priceColor: '#a5b4fc',
            badgeBg: '#7c3aed', border: 'none', bottomBorder: 'none', nameColor: '#e0e7ff', weightColor: '#6366f1',
        },
        'carbon-fiber': {
            bg: '#18181b', radius: 12, shadow: '0 4px 10px rgba(0,0,0,0.3)', imgH: 100, priceFw: 800, priceColor: '#a1a1aa',
            badgeBg: '#dc2626', border: '1px solid #3f3f46', bottomBorder: 'none', nameColor: '#fafafa', weightColor: '#71717a',
        },
        'frosted-mint': {
            bg: '#ecfdf5', radius: 20, shadow: '0 2px 10px rgba(16,185,129,0.08)', imgH: 100, priceFw: 800, priceColor: '#047857',
            badgeBg: '#059669', border: '1px solid #a7f3d0', bottomBorder: 'none', nameColor: '#064e3b', weightColor: '#6ee7b7',
        },
        'coral-reef': {
            bg: '#fff7ed', radius: 18, shadow: '0 3px 8px rgba(249,115,22,0.1)', imgH: 100, priceFw: 800, priceColor: '#ea580c',
            badgeBg: '#f97316', border: 'none', bottomBorder: '3px solid #f97316', nameColor: '#9a3412', weightColor: '#fdba74',
        },
        'lavender-mist': {
            bg: '#faf5ff', radius: 20, shadow: '0 2px 8px rgba(124,58,237,0.06)', imgH: 100, priceFw: 800, priceColor: '#7c3aed',
            badgeBg: '#7c3aed', border: '1px solid #e9d5ff', bottomBorder: 'none', nameColor: '#581c87', weightColor: '#d8b4fe',
        },
        'woodland-earth': {
            bg: '#faf7f5', radius: 14, shadow: 'none', imgH: 100, priceFw: 800, priceColor: '#57534e',
            badgeBg: '#78716c', border: '1px solid #d6cfc7', bottomBorder: 'none', nameColor: '#44403c', weightColor: '#a8a29e',
        },
        'steel-industrial': {
            bg: '#f4f4f5', radius: 8, shadow: 'none', imgH: 100, priceFw: 900, priceColor: '#3f3f46',
            badgeBg: '#27272a', border: '2px solid #a1a1aa', bottomBorder: 'none', nameColor: '#27272a', weightColor: '#71717a',
        },
        'candy-pop': {
            bg: '#fef3c7', radius: 22, shadow: '0 3px 10px rgba(245,158,11,0.15)', imgH: 100, priceFw: 900, priceColor: '#b45309',
            badgeBg: '#f43f5e', border: '2px solid #fbbf24', bottomBorder: 'none', nameColor: '#92400e', weightColor: '#f59e0b',
        },
        'ocean-depth': {
            bg: '#0c4a6e', radius: 16, shadow: '0 4px 12px rgba(3,105,161,0.2)', imgH: 105, priceFw: 800, priceColor: '#38bdf8',
            badgeBg: '#0ea5e9', border: 'none', bottomBorder: 'none', nameColor: '#e0f2fe', weightColor: '#0284c7',
        },
        'rose-gold': {
            bg: '#1c1917', radius: 18, shadow: '0 3px 10px rgba(217,119,6,0.12)', imgH: 110, priceFw: 800, priceColor: '#fbbf24',
            badgeBg: '#d97706', border: '1px solid #b45309', bottomBorder: 'none', nameColor: '#fde68a', weightColor: '#78716c',
        },
        'emerald-city': {
            bg: '#064e3b', radius: 18, shadow: '0 4px 12px rgba(4,120,87,0.2)', imgH: 105, priceFw: 800, priceColor: '#34d399',
            badgeBg: '#10b981', border: 'none', bottomBorder: 'none', nameColor: '#d1fae5', weightColor: '#059669',
        },
        'watercolor-soft': {
            bg: '#fefce8', radius: 24, shadow: '0 3px 12px rgba(212,212,216,0.08)', imgH: 100, priceFw: 700, priceColor: '#854d0e',
            badgeBg: 'rgba(202,138,4,0.85)', border: '1px solid #fef08a', bottomBorder: 'none', nameColor: '#713f12', weightColor: '#a16207',
        },
        'blueprint-tech': {
            bg: '#172554', radius: 10, shadow: 'none', imgH: 100, priceFw: 800, priceColor: '#60a5fa',
            badgeBg: '#2563eb', border: '1px solid #1e40af', bottomBorder: 'none', nameColor: '#93c5fd', weightColor: '#3b82f6',
        },
        'split-layout': {
            bg: '#fff', radius: 16, shadow: '0 2px 8px rgba(100,116,139,0.06)', imgH: 90, priceFw: 800, priceColor: '#0f172a',
            badgeBg: '#ef4444', border: '1px solid #f1f5f9', bottomBorder: 'none', nameColor: '#0f172a', weightColor: '#94a3b8',
        },
    };
    const c = cards[style] || cards['classic-grid'];

    return (
        <div style={{
            width: '100%', maxWidth: 200, margin: '0 auto',
            background: '#f9fafb', borderRadius: 20, padding: '12px 8px', border: '2px solid #e5e7eb',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        }}>
            {/* Phone header bar */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, padding: '0 4px' }}>
                <div style={{ width: 40, height: 4, borderRadius: 2, background: '#d1d5db' }} />
                <div style={{ width: 6, height: 6, borderRadius: 3, background: '#d1d5db' }} />
            </div>
            {/* Mini product cards grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {[1, 2].map(i => (
                    <div key={i} style={{
                        background: c.bg, borderRadius: c.radius / 2, overflow: 'hidden',
                        boxShadow: c.shadow, border: c.border, borderBottom: c.bottomBorder,
                    }}>
                        <div style={{
                            height: c.imgH / 2, background: i === 1 ? '#dbeafe' : '#fce7f3', position: 'relative',
                        }}>
                            {c.hasGradientOverlay && (
                                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 15, background: `linear-gradient(transparent, ${c.bg})` }} />
                            )}
                            {c.badgeBg !== 'transparent' && (
                                <div style={{
                                    position: 'absolute', top: 3, right: 3, background: c.badgeBg,
                                    borderRadius: 6, padding: '1px 4px', fontSize: 6, color: '#fff', fontWeight: 700,
                                }}>20%</div>
                            )}
                        </div>
                        <div style={{ padding: '4px 5px 6px' }}>
                            <div style={{ height: 5, width: '80%', borderRadius: 2, background: c.nameColor, opacity: 0.2, marginBottom: 3 }} />
                            <div style={{ height: 4, width: '50%', borderRadius: 2, background: c.weightColor, opacity: 0.3, marginBottom: 3 }} />
                            <div style={{ display: 'flex', gap: 3, alignItems: 'baseline' }}>
                                <div style={{ fontSize: 8, fontWeight: c.priceFw, color: c.priceColor }}>₹299</div>
                                <div style={{ fontSize: 6, color: '#9ca3af', textDecoration: 'line-through' }}>₹399</div>
                                {c.discountInline && <div style={{ fontSize: 5, color: c.discountColor, fontWeight: 700 }}>25% off</div>}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 6 }}>
                {[3, 4].map(i => (
                    <div key={i} style={{
                        background: c.bg, borderRadius: c.radius / 2, overflow: 'hidden',
                        boxShadow: c.shadow, border: c.border, borderBottom: c.bottomBorder,
                    }}>
                        <div style={{ height: c.imgH / 2, background: i === 3 ? '#d1fae5' : '#fef3c7' }}>
                            {c.hasGradientOverlay && (
                                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 15, background: `linear-gradient(transparent, ${c.bg})` }} />
                            )}
                        </div>
                        <div style={{ padding: '4px 5px 6px' }}>
                            <div style={{ height: 5, width: '70%', borderRadius: 2, background: c.nameColor, opacity: 0.2, marginBottom: 3 }} />
                            <div style={{ height: 4, width: '40%', borderRadius: 2, background: c.weightColor, opacity: 0.3, marginBottom: 3 }} />
                            <div style={{ display: 'flex', gap: 3, alignItems: 'baseline' }}>
                                <div style={{ fontSize: 8, fontWeight: c.priceFw, color: c.priceColor }}>₹199</div>
                                <div style={{ fontSize: 6, color: '#9ca3af', textDecoration: 'line-through' }}>₹249</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// =============================================
// MINI PREVIEW RENDERERS (admin phone mockup)
// =============================================
const LayoutPreview = ({ layoutType, categories }) => {
    const previewCats = categories.slice(0, 6);
    if (!previewCats.length) return <div className="text-[9px] text-gray-400 text-center py-3">No visible categories</div>;

    const renderDefault = () => (
        <div className="flex gap-3 overflow-x-auto pb-2 px-2">
            {previewCats.map((c, i) => (
                <div key={i} className="flex flex-col items-center min-w-[56px]">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 border-2 border-blue-300 overflow-hidden">
                        {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-cover" /> :
                            <div className="w-full h-full flex items-center justify-center text-blue-400 text-xs">IMG</div>}
                    </div>
                    <span className="text-[9px] text-gray-600 mt-1 text-center truncate w-14">{c.categoryName}</span>
                </div>
            ))}
        </div>
    );

    const renderGrid = (cols) => (
        <div className={`grid gap-1.5 px-2`} style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
            {previewCats.slice(0, cols * 2).map((c, i) => (
                <div key={i} className="rounded-md bg-gray-100 overflow-hidden aspect-square relative">
                    {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-cover" /> :
                        <div className="w-full h-full flex items-center justify-center text-gray-400 text-[8px]">IMG</div>}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/40 px-0.5 py-0.5">
                        <span className="text-[7px] text-white truncate block text-center">{c.categoryName}</span>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderHorizCards = (className) => (
        <div className="flex gap-2 overflow-x-auto pb-2 px-2">
            {previewCats.map((c, i) => (
                <div key={i} className={`min-w-[72px] h-20 rounded-lg relative overflow-hidden ${className}`}>
                    {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gray-200" />}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-1.5">
                        <span className="text-[8px] text-white font-medium truncate">{c.categoryName}</span>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderPills = (bg, text) => (
        <div className="flex flex-wrap gap-1.5 px-2">
            {previewCats.map((c, i) => (
                <div key={i} className={`flex items-center gap-1 ${bg} rounded-full px-2 py-1 border`}>
                    <div className="w-4 h-4 rounded-full bg-gray-200 overflow-hidden">
                        {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-cover" /> : null}
                    </div>
                    <span className={`text-[8px] font-medium ${text}`}>{c.categoryName}</span>
                </div>
            ))}
        </div>
    );

    const renderBannerList = () => (
        <div className="space-y-1.5 px-2">
            {previewCats.slice(0, 3).map((c, i) => (
                <div key={i} className="h-8 rounded-lg bg-gradient-to-r from-gray-800 to-gray-600 relative overflow-hidden flex items-center px-3">
                    {c.preview && <img src={c.preview} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
                    <span className="text-[8px] text-white font-bold relative z-10">{c.categoryName}</span>
                    <span className="ml-auto text-[7px] text-white/70 relative z-10">&#8594;</span>
                </div>
            ))}
        </div>
    );

    const renderIconGrid = () => (
        <div className="grid grid-cols-4 gap-2 px-2">
            {previewCats.slice(0, 4).map((c, i) => (
                <div key={i} className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 border border-purple-200 overflow-hidden">
                        {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-cover" /> : <span className="text-sm flex items-center justify-center h-full">&#128230;</span>}
                    </div>
                    <span className="text-[7px] text-gray-600 mt-0.5 text-center truncate w-12">{c.categoryName}</span>
                </div>
            ))}
        </div>
    );

    const renderMasonry = () => (
        <div className="flex gap-1.5 px-2">
            {[0, 1].map(col => (
                <div key={col} className="flex flex-col gap-1.5 flex-1" style={col === 1 ? { marginTop: 8 } : {}}>
                    {previewCats.filter((_, i) => i % 2 === col).slice(0, 2).map((c, i) => (
                        <div key={i} className={`rounded-lg bg-gray-200 relative overflow-hidden ${(col + i) % 2 === 0 ? 'h-16' : 'h-10'}`}>
                            {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-cover" /> : null}
                            <div className="absolute bottom-0 left-0 right-0 bg-black/40 px-1 py-0.5"><span className="text-[7px] text-white">{c.categoryName}</span></div>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );

    const renderBubbles = () => (
        <div className="flex flex-wrap gap-2 px-2 justify-center py-1">
            {previewCats.map((c, i) => {
                const sizes = ['w-10 h-10', 'w-12 h-12', 'w-9 h-9', 'w-11 h-11', 'w-10 h-10', 'w-8 h-8'];
                return (
                    <div key={i} className="flex flex-col items-center">
                        <div className={`${sizes[i] || 'w-10 h-10'} rounded-full bg-gradient-to-br from-teal-100 to-cyan-200 border-2 border-teal-300 overflow-hidden`}>
                            {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-cover" /> : null}
                        </div>
                        <span className="text-[7px] text-gray-600 mt-0.5">{c.categoryName}</span>
                    </div>
                );
            })}
        </div>
    );

    const renderDiamond = () => (
        <div className="flex flex-wrap gap-3 px-4 justify-center py-2">
            {previewCats.slice(0, 4).map((c, i) => (
                <div key={i} className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-gray-200 overflow-hidden border border-gray-300" style={{ transform: 'rotate(45deg)', borderRadius: 6 }}>
                        {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-cover" style={{ transform: 'rotate(-45deg) scale(1.41)' }} /> : null}
                    </div>
                    <span className="text-[7px] text-gray-600 mt-2">{c.categoryName}</span>
                </div>
            ))}
        </div>
    );

    const renderHexagon = () => (
        <div className="flex flex-wrap gap-2 px-3 justify-center py-2">
            {previewCats.slice(0, 4).map((c, i) => (
                <div key={i} className="flex flex-col items-center">
                    <div className="w-12 h-11 bg-gray-200 overflow-hidden" style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)' }}>
                        {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-cover" /> : null}
                    </div>
                    <span className="text-[7px] text-gray-600 mt-1">{c.categoryName}</span>
                </div>
            ))}
        </div>
    );

    const renderStoryCircles = () => (
        <div className="flex gap-3 overflow-x-auto pb-2 px-2">
            {previewCats.map((c, i) => (
                <div key={i} className="flex flex-col items-center min-w-[50px]">
                    <div className="w-12 h-12 rounded-full p-0.5 bg-gradient-to-tr from-pink-500 via-red-500 to-yellow-500">
                        <div className="w-full h-full rounded-full bg-white p-0.5 overflow-hidden">
                            {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-cover rounded-full" /> : <div className="w-full h-full bg-gray-200 rounded-full" />}
                        </div>
                    </div>
                    <span className="text-[8px] text-gray-600 mt-1 truncate w-12 text-center">{c.categoryName}</span>
                </div>
            ))}
        </div>
    );

    const renderMinimalText = () => (
        <div className="space-y-1 px-3">
            {previewCats.slice(0, 4).map((c, i) => (
                <div key={i} className="flex items-center justify-between py-1 border-b border-gray-100">
                    <span className="text-[9px] font-semibold text-gray-700">{c.categoryName}</span>
                    <span className="text-[8px] text-gray-400">&#8594;</span>
                </div>
            ))}
        </div>
    );

    const renderSplitBanner = () => (
        <div className="flex gap-1.5 px-2">
            {previewCats.slice(0, 2).map((c, i) => (
                <div key={i} className="flex-1 h-20 rounded-lg bg-gray-200 relative overflow-hidden">
                    {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-cover" /> : null}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-2">
                        <span className="text-[8px] text-white font-bold">{c.categoryName}</span>
                    </div>
                </div>
            ))}
        </div>
    );

    const renderMetroTiles = () => (
        <div className="grid grid-cols-3 grid-rows-2 gap-1 px-2" style={{ height: 80 }}>
            {previewCats.slice(0, 3).map((c, i) => (
                <div key={i} className={`rounded bg-gray-200 relative overflow-hidden ${i === 0 ? 'row-span-2' : ''}`}>
                    {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-cover" /> : null}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/40 px-0.5 py-0.5"><span className="text-[6px] text-white">{c.categoryName}</span></div>
                </div>
            ))}
        </div>
    );

    const renderZigzag = () => (
        <div className="space-y-1 px-2">
            {previewCats.slice(0, 3).map((c, i) => (
                <div key={i} className={`flex items-center gap-2 ${i % 2 === 1 ? 'flex-row-reverse' : ''}`}>
                    <div className="w-12 h-8 rounded bg-gray-200 overflow-hidden flex-shrink-0">
                        {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-cover" /> : null}
                    </div>
                    <span className="text-[8px] font-medium text-gray-700">{c.categoryName}</span>
                </div>
            ))}
        </div>
    );

    const renderSpotlightDuo = () => (
        <div className="flex gap-1.5 px-2" style={{ height: 80 }}>
            <div className="flex-[2] rounded-lg bg-gray-200 relative overflow-hidden">
                {previewCats[0]?.preview ? <img src={previewCats[0].preview} alt="" className="w-full h-full object-cover" /> : null}
                <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-2 py-1"><span className="text-[8px] text-white font-bold">{previewCats[0]?.categoryName}</span></div>
            </div>
            <div className="flex-1 flex flex-col gap-1.5">
                {previewCats.slice(1, 3).map((c, i) => (
                    <div key={i} className="flex-1 rounded-lg bg-gray-200 relative overflow-hidden">
                        {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-cover" /> : null}
                        <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-1 py-0.5"><span className="text-[7px] text-white">{c.categoryName}</span></div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderOverlapScroll = () => (
        <div className="flex overflow-x-auto pb-2 px-2">
            {previewCats.map((c, i) => (
                <div key={i} className="min-w-[60px] h-16 rounded-lg relative overflow-hidden border-2 border-white shadow-md bg-gray-200" style={{ marginLeft: i > 0 ? -12 : 0, zIndex: previewCats.length - i }}>
                    {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-cover" /> : null}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-1 py-0.5"><span className="text-[7px] text-white">{c.categoryName}</span></div>
                </div>
            ))}
        </div>
    );

    const renderFilmStrip = () => (
        <div className="flex gap-1 overflow-x-auto pb-1 px-2 bg-gray-900 rounded-md py-1.5">
            {previewCats.map((c, i) => (
                <div key={i} className="min-w-[56px] flex flex-col items-center">
                    <div className="flex gap-0.5 mb-0.5">{[...Array(4)].map((_, j) => <div key={j} className="w-1 h-1 bg-gray-600 rounded-sm" />)}</div>
                    <div className="w-14 h-10 rounded-sm bg-gray-700 overflow-hidden">
                        {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-cover" /> : null}
                    </div>
                    <div className="flex gap-0.5 mt-0.5">{[...Array(4)].map((_, j) => <div key={j} className="w-1 h-1 bg-gray-600 rounded-sm" />)}</div>
                    <span className="text-[6px] text-gray-400 mt-0.5">{c.categoryName}</span>
                </div>
            ))}
        </div>
    );

    const renderAccordion = () => (
        <div className="space-y-1 px-2">
            {previewCats.slice(0, 4).map((c, i) => (
                <div key={i} className="border border-gray-200 rounded-md overflow-hidden">
                    <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50">
                        <div className="w-5 h-5 rounded bg-gray-200 overflow-hidden">{c.preview ? <img src={c.preview} alt="" className="w-full h-full object-cover" /> : null}</div>
                        <span className="text-[8px] font-medium text-gray-700 flex-1">{c.categoryName}</span>
                        <FiChevronDown className="text-gray-400" style={{ fontSize: 8 }} />
                    </div>
                </div>
            ))}
        </div>
    );

    const renderGradientWave = () => {
        const gradients = ['from-pink-300 to-purple-400', 'from-cyan-300 to-blue-400', 'from-amber-300 to-orange-400', 'from-green-300 to-teal-400'];
        return (
            <div className="flex gap-2 overflow-x-auto pb-2 px-2">
                {previewCats.map((c, i) => (
                    <div key={i} className={`min-w-[72px] h-20 rounded-xl bg-gradient-to-br ${gradients[i % gradients.length]} relative overflow-hidden`}>
                        <div className="absolute inset-0 flex items-end p-2">
                            <span className="text-[8px] text-white font-bold drop-shadow">{c.categoryName}</span>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderRoundedBoxGrid = () => (
        <div className="grid grid-cols-4 gap-1.5 px-2">
            {previewCats.slice(0, 4).map((c, i) => (
                <div key={i} className="flex flex-col items-center">
                    <div className="w-full aspect-square rounded-xl bg-gray-100 relative overflow-hidden flex items-center justify-center p-1">
                        {i < 2 && <span className="absolute top-0.5 left-0.5 bg-violet-600 text-white text-[5px] font-bold px-1 rounded">NEW</span>}
                        {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-contain" /> : <span className="text-[8px] text-gray-400">IMG</span>}
                    </div>
                    <span className="text-[6px] text-gray-700 mt-0.5 text-center truncate w-full font-medium">{c.categoryName}</span>
                </div>
            ))}
        </div>
    );

    const renderIsometricStore = () => {
        const colors = ['#F5E6D3', '#FCE7F3', '#DBEAFE', '#D1FAE5'];
        return (
            <div className="grid grid-cols-4 gap-1.5 px-2">
                {previewCats.slice(0, 4).map((c, i) => (
                    <div key={i} className="flex flex-col items-center">
                        <div className="w-full aspect-square flex items-end justify-center pb-1">
                            <div className="w-[82%] h-[78%] rounded-xl flex items-center justify-center p-1 shadow-md" style={{ backgroundColor: colors[i % colors.length] }}>
                                {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-contain" /> : <span className="text-[8px] text-gray-400">IMG</span>}
                            </div>
                        </div>
                        <span className="text-[6px] text-gray-700 text-center truncate w-full font-semibold">{c.categoryName}</span>
                    </div>
                ))}
            </div>
        );
    };

    const renderGroupedPastel = () => (
        <div className="px-2 space-y-2">
            <div className="text-[8px] font-bold text-gray-800">Grocery</div>
            <div className="grid grid-cols-4 gap-1.5">
                {previewCats.slice(0, 4).map((c, i) => (
                    <div key={i} className="flex flex-col items-center">
                        <div className="w-full aspect-square rounded-xl flex items-center justify-center p-1" style={{ backgroundColor: ['#FDF2F8', '#FFF7ED', '#F0FDF4', '#EFF6FF'][i] }}>
                            {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-contain" /> : <span className="text-[8px] text-gray-400">IMG</span>}
                        </div>
                        <span className="text-[5px] text-gray-700 mt-0.5 text-center truncate w-full">{c.categoryName}</span>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderQuickMinutes = () => (
        <div className="mx-2 rounded-xl bg-white border border-gray-100 p-2 shadow-sm">
            <div className="grid grid-cols-4 gap-1.5">
                {previewCats.slice(0, 4).map((c, i) => (
                    <div key={i} className="flex flex-col items-center">
                        <div className="w-full aspect-square rounded-xl flex items-center justify-center p-1" style={{ backgroundColor: ['#FDF2F8', '#ECFEFF', '#FEFCE8', '#F0FDF4'][i] }}>
                            {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-contain" /> : null}
                        </div>
                        <span className="text-[5px] text-gray-700 mt-0.5 text-center truncate w-full">{c.categoryName}</span>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderZeptoMosaic = () => (
        <div className="px-2 space-y-1.5">
            <div className="flex gap-1.5 h-14">
                <div className="flex-[2] rounded-lg bg-white/80 border border-pink-100 overflow-hidden flex flex-col">
                    <span className="text-[6px] font-bold text-center pt-0.5 truncate px-1">{previewCats[0]?.categoryName}</span>
                    <div className="flex-1 flex items-center justify-center">{previewCats[0]?.preview ? <img src={previewCats[0].preview} alt="" className="h-6 object-contain" /> : null}</div>
                    <div className="bg-pink-500 text-white text-[5px] font-bold text-center py-0.5">Top Deals</div>
                </div>
                <div className="flex-1 flex flex-col gap-1.5">
                    {previewCats.slice(1, 3).map((c, i) => (
                        <div key={i} className="flex-1 rounded-lg bg-white/80 border border-pink-100 overflow-hidden flex flex-col">
                            <span className="text-[5px] font-bold text-center truncate px-0.5">{c.categoryName}</span>
                            <div className="flex-1 flex items-center justify-center">{c.preview ? <img src={c.preview} alt="" className="h-4 object-contain" /> : null}</div>
                            <div className="bg-pink-500 text-white text-[4px] font-bold text-center py-0.5">50% Off</div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="grid grid-cols-4 gap-1">
                {previewCats.slice(0, 4).map((c, i) => (
                    <div key={i} className="rounded-md bg-white/80 border border-pink-100 overflow-hidden">
                        <div className="text-[4px] font-bold text-center truncate px-0.5 pt-0.5">{c.categoryName}</div>
                        <div className="h-5 flex items-center justify-center">{c.preview ? <img src={c.preview} alt="" className="h-4 object-contain" /> : null}</div>
                        <div className="bg-pink-500 text-white text-[4px] text-center py-0.5">Shop</div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderCloudGlass = () => (
        <div className="mx-2 rounded-2xl bg-gradient-to-br from-pink-100 via-sky-100 to-pink-50 p-2">
            <div className="grid grid-cols-4 gap-1.5">
                {previewCats.slice(0, 4).map((c, i) => (
                    <div key={i} className="rounded-xl overflow-hidden bg-white/50 border border-white/70 backdrop-blur-sm">
                        <div className="text-[5px] font-bold text-center truncate px-0.5 pt-1">{c.categoryName}</div>
                        <div className="h-8 flex items-center justify-center">{c.preview ? <img src={c.preview} alt="" className="h-6 object-contain" /> : null}</div>
                        <div className="bg-pink-500 text-white text-[4px] font-bold text-center py-0.5">{DEFAULT_CATEGORY_PROMO_LABELS[i % DEFAULT_CATEGORY_PROMO_LABELS.length]}</div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderGrid4Fixed = (variant = 'border') => (
        <div className="grid grid-cols-4 gap-1 px-2">
            {previewCats.slice(0, 4).map((c, i) => (
                <div key={i} className="flex flex-col items-center">
                    <div className={`w-full aspect-square rounded-lg overflow-hidden flex items-center justify-center p-0.5 ${
                        variant === 'shadow' ? 'bg-white shadow-sm' :
                        variant === 'gradient' ? 'bg-gradient-to-br from-pink-100 to-blue-100' :
                        variant === 'ink' ? 'bg-white border border-gray-200' :
                        variant === 'halo' ? 'bg-gray-50 border border-gray-100' :
                        variant === 'boutique' ? 'bg-amber-50 border-2 border-amber-400 p-0.5' :
                        'bg-white border border-gray-200'
                    }`}>
                        {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-contain" /> : <span className="text-[7px] text-gray-400">IMG</span>}
                    </div>
                    <span className={`text-[5px] mt-0.5 text-center truncate w-full ${variant === 'ink' ? 'font-bold text-gray-900' : 'text-gray-700'}`}>{c.categoryName}</span>
                </div>
            ))}
        </div>
    );

    const renderGrid3Fixed = (variant = 'overlay') => (
        <div className="grid grid-cols-3 gap-1 px-2">
            {previewCats.slice(0, 3).map((c, i) => (
                <div key={i} className="flex flex-col items-center">
                    <div className={`w-full aspect-[0.95] rounded-lg overflow-hidden relative ${
                        variant === 'lifted' ? 'bg-gray-50 shadow-sm border border-gray-100' :
                        variant === 'pastel' ? 'bg-pink-50' :
                        variant === 'boutique' ? 'bg-amber-50 border-2 border-amber-400 p-0.5' :
                        variant === 'slate' ? 'bg-slate-50 border border-slate-200' :
                        'bg-gray-200'
                    }`}>
                        {c.preview ? <img src={c.preview} alt="" className="w-full h-full object-contain p-0.5" /> : null}
                        {variant === 'overlay' && (
                            <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-0.5 py-0.5">
                                <span className="text-[5px] text-white font-bold truncate block">{c.categoryName}</span>
                            </div>
                        )}
                    </div>
                    {variant !== 'overlay' && (
                        <span className="text-[5px] text-gray-700 mt-0.5 text-center truncate w-full font-semibold">{c.categoryName}</span>
                    )}
                </div>
            ))}
        </div>
    );

    const renderCloudGlass4Col = () => (
        <div className="bg-gradient-to-br from-pink-100 via-sky-100 to-pink-50 p-2">
            <div className="grid grid-cols-4 gap-1">
                {previewCats.slice(0, 4).map((c, i) => (
                    <div key={i} className="rounded-lg overflow-hidden bg-white/55 border border-white/80">
                        <div className="text-[4px] font-bold text-center truncate px-0.5 pt-0.5">{c.categoryName}</div>
                        <div className="h-7 flex items-center justify-center">{c.preview ? <img src={c.preview} alt="" className="h-5 object-contain" /> : null}</div>
                        <div className="bg-pink-500 text-white text-[4px] text-center py-0.5">{DEFAULT_CATEGORY_PROMO_LABELS[i % DEFAULT_CATEGORY_PROMO_LABELS.length]}</div>
                    </div>
                ))}
            </div>
        </div>
    );

    const previews = {
        'circle-scroll': renderDefault,
        'grid-2col': () => renderGrid(2),
        'grid-3col': () => renderGrid(3),
        'card-scroll': () => renderHorizCards('bg-gradient-to-br from-gray-200 to-gray-300'),
        'pill-tags': () => renderPills('bg-blue-50 border-blue-200', 'text-blue-700'),
        'banner-list': renderBannerList,
        'icon-grid': renderIconGrid,
        'masonry': renderMasonry,
        'carousel-featured': () => renderHorizCards('bg-gradient-to-br from-indigo-400 to-purple-500'),
        'bubble-float': renderBubbles,
        'diamond-grid': renderDiamond,
        'hexagon-grid': renderHexagon,
        'wave-scroll': () => renderHorizCards('bg-gradient-to-br from-cyan-200 to-blue-300'),
        'polaroid-scroll': () => renderHorizCards('border-4 border-white shadow-md bg-white'),
        'glass-cards': () => renderHorizCards('bg-white/50 border border-white/60'),
        'neon-pills': () => renderPills('bg-gray-900 border-cyan-400', 'text-cyan-400'),
        'story-circles': renderStoryCircles,
        'ribbon-cards': () => renderHorizCards('bg-gradient-to-br from-amber-100 to-orange-200'),
        'spotlight-duo': renderSpotlightDuo,
        'film-strip': renderFilmStrip,
        'minimal-text': renderMinimalText,
        'overlap-scroll': renderOverlapScroll,
        'tag-cloud': () => renderPills('bg-gradient-to-r from-blue-50 to-purple-50 border-purple-200', 'text-purple-700'),
        'metro-tiles': renderMetroTiles,
        'zigzag-list': renderZigzag,
        'chip-scroll': () => renderPills('bg-white border-gray-300 shadow-sm', 'text-gray-700'),
        'accordion-list': renderAccordion,
        'flip-cards': () => renderHorizCards('bg-gradient-to-br from-emerald-200 to-teal-300'),
        'gradient-wave': renderGradientWave,
        'split-banner': renderSplitBanner,
        'carousel-stack': () => renderHorizCards('bg-gradient-to-br from-gray-300 to-gray-400 shadow-md'),
        'carousel-tinder': () => renderHorizCards('bg-gradient-to-br from-rose-200 to-pink-300 shadow-lg'),
        'carousel-3d': () => renderHorizCards('bg-gradient-to-br from-indigo-200 to-blue-300'),
        'carousel-coverflow': () => renderHorizCards('bg-gradient-to-br from-purple-200 to-indigo-300 shadow-md'),
        'carousel-minimap': () => renderHorizCards('bg-gradient-to-br from-teal-200 to-cyan-300'),
        'carousel-parallax': () => renderHorizCards('bg-gradient-to-br from-amber-200 to-yellow-300'),
        'carousel-zoom': () => renderHorizCards('bg-gradient-to-br from-green-200 to-emerald-300'),
        'carousel-book': () => renderHorizCards('bg-white border-2 border-gray-200 shadow'),
        'carousel-timeline': () => renderHorizCards('bg-gradient-to-br from-slate-200 to-gray-300'),
        'carousel-panoramic': () => renderHorizCards('bg-gradient-to-br from-sky-200 to-blue-300'),
        'rounded-box-grid': renderRoundedBoxGrid,
        'isometric-store-grid': renderIsometricStore,
        'grouped-pastel-grid': renderGroupedPastel,
        'quick-minutes-grid': renderQuickMinutes,
        'zepto-mosaic-grid': renderZeptoMosaic,
        'cloud-glass-grid': renderCloudGlass,
        'cloud-glass-4col-fixed': renderCloudGlass4Col,
        'crisp-border-4col': () => renderGrid4Fixed('border'),
        'soft-shadow-4col': () => renderGrid4Fixed('shadow'),
        'gradient-chip-4col': () => renderGrid4Fixed('gradient'),
        'ink-label-4col': () => renderGrid4Fixed('ink'),
        'halo-circle-4col': () => renderGrid4Fixed('halo'),
        'premium-overlay-3col': () => renderGrid3Fixed('overlay'),
        'lifted-card-3col': () => renderGrid3Fixed('lifted'),
        'pastel-block-3col': () => renderGrid3Fixed('pastel'),
        'boutique-frame-3col': () => renderGrid3Fixed('boutique'),
        'boutique-frame-4col': () => renderGrid4Fixed('boutique'),
        'slate-minimal-3col': () => renderGrid3Fixed('slate'),
    };

    const renderer = previews[layoutType];
    return renderer ? renderer() : renderDefault();
};

// =============================================
// SUB-CATEGORY PREVIEW
// =============================================
const SubLayoutPreview = ({ layoutType, items }) => {
    const previewItems = items.slice(0, 5);
    if (!previewItems.length) return <div className="text-[8px] text-gray-400 px-2">No sub-categories to preview</div>;

    const renderPillScroll = () => (
        <div className="flex gap-1.5 overflow-x-auto px-2">
            {previewItems.map((s, i) => (
                <div key={i} className="bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5 whitespace-nowrap">
                    <span className="text-[7px] text-blue-700 font-medium">{s.categoryName}</span>
                </div>
            ))}
        </div>
    );
    const renderCircleScroll = () => (
        <div className="flex gap-2 overflow-x-auto px-2">
            {previewItems.map((s, i) => (
                <div key={i} className="flex flex-col items-center min-w-[36px]">
                    <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden border border-gray-300">
                        {s.preview ? <img src={s.preview} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gray-200" />}
                    </div>
                    <span className="text-[6px] text-gray-500 mt-0.5 truncate w-9 text-center">{s.categoryName}</span>
                </div>
            ))}
        </div>
    );
    const renderChipScroll = () => (
        <div className="flex gap-1.5 overflow-x-auto px-2">
            {previewItems.map((s, i) => (
                <div key={i} className="flex items-center gap-0.5 bg-white border border-gray-200 rounded-full px-1.5 py-0.5 shadow-sm">
                    <div className="w-3 h-3 rounded-full bg-gray-200 overflow-hidden">{s.preview ? <img src={s.preview} alt="" className="w-full h-full object-cover" /> : null}</div>
                    <span className="text-[7px] text-gray-600">{s.categoryName}</span>
                </div>
            ))}
        </div>
    );
    const renderNeonScroll = () => (
        <div className="flex gap-1.5 overflow-x-auto px-2 py-1 bg-gray-900 rounded-md">
            {previewItems.map((s, i) => (
                <div key={i} className="border border-cyan-400 rounded-full px-2 py-0.5 whitespace-nowrap">
                    <span className="text-[7px] text-cyan-400 font-medium">{s.categoryName}</span>
                </div>
            ))}
        </div>
    );
    const renderGradientScroll = () => {
        const grads = ['from-pink-400 to-purple-500', 'from-blue-400 to-cyan-500', 'from-amber-400 to-orange-500', 'from-green-400 to-teal-500', 'from-red-400 to-pink-500'];
        return (
            <div className="flex gap-1.5 overflow-x-auto px-2">
                {previewItems.map((s, i) => (
                    <div key={i} className={`bg-gradient-to-r ${grads[i % grads.length]} rounded-lg px-2 py-1 whitespace-nowrap`}>
                        <span className="text-[7px] text-white font-medium">{s.categoryName}</span>
                    </div>
                ))}
            </div>
        );
    };
    const renderUnderlineScroll = () => (
        <div className="flex gap-3 overflow-x-auto px-2 border-b border-gray-200">
            {previewItems.map((s, i) => (
                <div key={i} className={`pb-1 whitespace-nowrap ${i === 0 ? 'border-b-2 border-blue-500' : ''}`}>
                    <span className={`text-[8px] font-medium ${i === 0 ? 'text-blue-600' : 'text-gray-500'}`}>{s.categoryName}</span>
                </div>
            ))}
        </div>
    );

    const previews = {
        'sub-pill-scroll': renderPillScroll,
        'sub-chip-scroll': renderChipScroll,
        'sub-circle-scroll': renderCircleScroll,
        'sub-card-scroll': () => renderHorizCardsSub(),
        'sub-tag-scroll': renderPillScroll,
        'sub-bubble-scroll': renderCircleScroll,
        'sub-glass-scroll': renderChipScroll,
        'sub-neon-scroll': renderNeonScroll,
        'sub-gradient-scroll': renderGradientScroll,
        'sub-underline-scroll': renderUnderlineScroll,
        'sub-diamond-scroll': renderChipScroll,
        'sub-outline-scroll': renderPillScroll,
        'sub-shadow-scroll': renderPillScroll,
        'sub-badge-scroll': renderChipScroll,
        'sub-ribbon-scroll': renderPillScroll,
        'sub-dot-scroll': renderUnderlineScroll,
        'sub-square-scroll': renderCircleScroll,
        'sub-icon-label-scroll': renderChipScroll,
        'sub-wave-scroll': renderPillScroll,
        'sub-pastel-scroll': renderGradientScroll,
        'sub-metro-scroll': renderPillScroll,
        'sub-minimal-scroll': renderUnderlineScroll,
        'sub-rounded-scroll': renderPillScroll,
        'sub-bold-scroll': renderUnderlineScroll,
        'sub-accent-scroll': renderPillScroll,
        'sub-float-scroll': renderChipScroll,
        'sub-story-scroll': renderCircleScroll,
        'sub-modern-scroll': renderChipScroll,
        'sub-elegant-scroll': renderPillScroll,
        'sub-vibrant-scroll': renderGradientScroll,
        'sub-slate-scroll': renderNeonScroll,
        'sub-aurora-scroll': renderGradientScroll,
        'sub-neumorphic-scroll': renderPillScroll,
        'sub-frosted-scroll': renderChipScroll,
        'sub-tag-label-scroll': renderChipScroll,
        'sub-stack-scroll': renderPillScroll,
        'sub-capsule-scroll': renderPillScroll,
        'sub-polaroid-scroll': renderCircleScroll,
        'sub-split-scroll': renderPillScroll,
        'sub-glow-scroll': renderNeonScroll,
    };

    function renderHorizCardsSub() {
        return (
            <div className="flex gap-1.5 overflow-x-auto px-2">
                {previewItems.map((s, i) => (
                    <div key={i} className="min-w-[48px] h-12 rounded-md bg-gray-200 relative overflow-hidden">
                        {s.preview ? <img src={s.preview} alt="" className="w-full h-full object-cover" /> : null}
                        <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-0.5 py-0.5"><span className="text-[6px] text-white">{s.categoryName}</span></div>
                    </div>
                ))}
            </div>
        );
    }

    const fn = previews[layoutType];
    return fn ? fn() : renderPillScroll();
};

// =============================================
// MAIN COMPONENT
// =============================================
const CategoryCustomization = () => {
    const [categories, setCategories] = useState([]);
    const [allCategories, setAllCategories] = useState([]);
    const [layoutType, setLayoutType] = useState('circle-scroll');
    const [subLayoutType, setSubLayoutType] = useState('sub-pill-scroll');
    const [categoryOrder, setCategoryOrder] = useState([]);
    const [hiddenCategories, setHiddenCategories] = useState([]);
    const [subCategoryOrder, setSubCategoryOrder] = useState({});
    const [hiddenSubCategories, setHiddenSubCategories] = useState([]);
    const [expandedCategory, setExpandedCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [subDraggedIndex, setSubDraggedIndex] = useState(null);
    const [activeTab, setActiveTab] = useState('category');
    const [perCategorySubLayout, setPerCategorySubLayout] = useState({});
    const [selectedCategoryForSubs, setSelectedCategoryForSubs] = useState(null);
    const [productCardStyle, setProductCardStyle] = useState('classic-grid');
    const [navBarStyle, setNavBarStyle] = useState('floating-pill');
    const [homeThemeId, setHomeThemeId] = useState('none');
    const [homeThemeSaving, setHomeThemeSaving] = useState(false);
    const [themeFilterCategory, setThemeFilterCategory] = useState('All');
    const [categoryPromoLabels, setCategoryPromoLabels] = useState([...DEFAULT_CATEGORY_PROMO_LABELS]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                const [catRes, custRes] = await Promise.all([
                    getCategories({}),
                    getCategoryCustomization({}),
                ]);

                let allCats = [];
                if (catRes?.success && Array.isArray(catRes.data)) {
                    allCats = catRes.data.filter(c => c.isActive !== false);
                }

                // The API returns hierarchical data: parent categories with nested subcategories array.
                // Flatten all subcategories into allCats so getSubCategories() can find them.
                const flatAll = [];
                allCats.forEach(parent => {
                    flatAll.push(parent);
                    if (Array.isArray(parent.subcategories)) {
                        parent.subcategories.forEach(sub => {
                            flatAll.push({ ...sub, parentCategoryId: parent._id });
                        });
                    }
                });
                setAllCategories(flatAll);

                let parentCats = allCats.filter(c => !c.parentCategoryId);
                setCategories(parentCats);

                if (custRes?.success && custRes.data) {
                    setLayoutType(custRes.data.layoutType || 'circle-scroll');
                    setSubLayoutType(custRes.data.subLayoutType || 'sub-pill-scroll');
                    setHiddenCategories(
                        Array.isArray(custRes.data.hiddenCategories) ? custRes.data.hiddenCategories.map(id => String(id)) : []
                    );
                    setHiddenSubCategories(
                        Array.isArray(custRes.data.hiddenSubCategories) ? custRes.data.hiddenSubCategories.map(id => String(id)) : []
                    );

                    if (Array.isArray(custRes.data.categoryOrder) && custRes.data.categoryOrder.length > 0) {
                        const orderMap = {};
                        custRes.data.categoryOrder.forEach(item => { orderMap[String(item.categoryId)] = item.sortOrder; });
                        parentCats.sort((a, b) => {
                            const oA = orderMap[String(a._id)] !== undefined ? orderMap[String(a._id)] : 9999;
                            const oB = orderMap[String(b._id)] !== undefined ? orderMap[String(b._id)] : 9999;
                            return oA - oB;
                        });
                        setCategories([...parentCats]);
                    }

                    if (custRes.data.subCategoryOrder && typeof custRes.data.subCategoryOrder === 'object') {
                        setSubCategoryOrder(custRes.data.subCategoryOrder);
                    }
                    if (custRes.data.perCategorySubLayout && typeof custRes.data.perCategorySubLayout === 'object') {
                        setPerCategorySubLayout(custRes.data.perCategorySubLayout);
                    }
                    if (custRes.data.productCardStyle) {
                        setProductCardStyle(custRes.data.productCardStyle);
                    }
                    if (custRes.data.navBarStyle) {
                        setNavBarStyle(custRes.data.navBarStyle);
                    }
                    if (Array.isArray(custRes.data.categoryPromoLabels) && custRes.data.categoryPromoLabels.length > 0) {
                        setCategoryPromoLabels(custRes.data.categoryPromoLabels);
                    } else {
                        setCategoryPromoLabels([...DEFAULT_CATEGORY_PROMO_LABELS]);
                    }
                }

                // Load home theme separately
                try {
                    const themeRes = await getHomeTheme();
                    if (themeRes?.success && themeRes.data?.themeId) {
                        setHomeThemeId(themeRes.data.themeId);
                    }
                } catch (e) { /* ignore - default 'none' is fine */ }
            } catch (err) {
                console.error("Error loading category customization:", err);
            } finally {
                setLoading(false);
            }
        };
        init();
    }, []);

    const visibleCategories = categories.filter(c => !hiddenCategories.includes(String(c._id)));

    const getSubCategories = (parentId) => {
        let subs = allCategories.filter(c => String(c.parentCategoryId) === String(parentId));
        const orderArr = subCategoryOrder[String(parentId)];
        if (Array.isArray(orderArr) && orderArr.length > 0) {
            const orderMap = {};
            orderArr.forEach(item => { orderMap[String(item.categoryId)] = item.sortOrder; });
            subs.sort((a, b) => {
                const oA = orderMap[String(a._id)] !== undefined ? orderMap[String(a._id)] : 9999;
                const oB = orderMap[String(b._id)] !== undefined ? orderMap[String(b._id)] : 9999;
                return oA - oB;
            });
        }
        return subs;
    };

    const getVisibleSubCategories = (parentId) => {
        return getSubCategories(parentId).filter(s => !hiddenSubCategories.includes(String(s._id)));
    };

    const markChanged = () => { setHasChanges(true); setIsSaved(false); };

    const updatePromoLabel = (index, value) => {
        setCategoryPromoLabels((prev) => prev.map((label, i) => (i === index ? value : label)));
        markChanged();
    };

    const addPromoLabel = () => {
        setCategoryPromoLabels((prev) => [...prev, 'New Offer']);
        markChanged();
    };

    const removePromoLabel = (index) => {
        setCategoryPromoLabels((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
        markChanged();
    };

    const resetPromoLabels = () => {
        setCategoryPromoLabels([...DEFAULT_CATEGORY_PROMO_LABELS]);
        markChanged();
    };

    const handleLayoutChange = (type) => { setLayoutType(type); markChanged(); };
    const handleSubLayoutChange = (type) => { setSubLayoutType(type); markChanged(); };
    const handlePerCategorySubLayoutChange = (parentId, type) => {
        setPerCategorySubLayout(prev => ({ ...prev, [String(parentId)]: type }));
        markChanged();
    };
    const handleProductCardStyleChange = (style) => { setProductCardStyle(style); markChanged(); };
    const handleNavBarStyleChange = (style) => { setNavBarStyle(style); markChanged(); };
    const getCategorySubLayout = (parentId) => perCategorySubLayout[String(parentId)] || subLayoutType;

    const handleHomeThemeSave = async (themeId) => {
        setHomeThemeId(themeId);
        setHomeThemeSaving(true);
        try {
            await saveHomeTheme({ themeId });
        } catch (err) {
            console.error("Error saving home theme:", err);
        } finally {
            setHomeThemeSaving(false);
        }
    };

    const moveCategory = (index, direction) => {
        const newCats = [...categories];
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= newCats.length) return;
        [newCats[index], newCats[newIndex]] = [newCats[newIndex], newCats[index]];
        setCategories(newCats);
        markChanged();
    };

    const moveSubCategory = (parentId, index, direction) => {
        const subs = getSubCategories(parentId);
        const newSubs = [...subs];
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= newSubs.length) return;
        [newSubs[index], newSubs[newIndex]] = [newSubs[newIndex], newSubs[index]];
        const newOrder = { ...subCategoryOrder };
        newOrder[String(parentId)] = newSubs.map((s, i) => ({ categoryId: String(s._id), sortOrder: i }));
        setSubCategoryOrder(newOrder);
        markChanged();
    };

    const toggleCategoryVisibility = (categoryId) => {
        const id = String(categoryId);
        setHiddenCategories(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
        markChanged();
    };

    const toggleSubCategoryVisibility = (subId) => {
        const id = String(subId);
        setHiddenSubCategories(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
        markChanged();
    };

    const handleDragStart = (e, index) => { setDraggedIndex(index); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', index); e.target.style.opacity = '0.5'; };
    const handleDragEnd = (e) => { e.target.style.opacity = '1'; setDraggedIndex(null); };
    const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === dropIndex) return;
        const newCats = [...categories];
        const [draggedItem] = newCats.splice(draggedIndex, 1);
        newCats.splice(dropIndex, 0, draggedItem);
        setCategories(newCats);
        setDraggedIndex(null);
        markChanged();
    };

    const handleSubDragStart = (e, index) => { setSubDraggedIndex(index); e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', index); e.target.style.opacity = '0.5'; };
    const handleSubDragEnd = (e) => { e.target.style.opacity = '1'; setSubDraggedIndex(null); };
    const handleSubDrop = (e, parentId, dropIndex) => {
        e.preventDefault();
        if (subDraggedIndex === null || subDraggedIndex === dropIndex) return;
        const subs = getSubCategories(parentId);
        const newSubs = [...subs];
        const [draggedItem] = newSubs.splice(subDraggedIndex, 1);
        newSubs.splice(dropIndex, 0, draggedItem);
        const newOrder = { ...subCategoryOrder };
        newOrder[String(parentId)] = newSubs.map((s, i) => ({ categoryId: String(s._id), sortOrder: i }));
        setSubCategoryOrder(newOrder);
        setSubDraggedIndex(null);
        markChanged();
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const orderData = categories.map((cat, index) => ({ categoryId: String(cat._id), sortOrder: index }));

            const finalSubOrder = {};
            categories.forEach(cat => {
                const subs = getSubCategories(cat._id);
                if (subs.length > 0) {
                    finalSubOrder[String(cat._id)] = subs.map((s, i) => ({ categoryId: String(s._id), sortOrder: i }));
                }
            });

            await saveCategoryCustomization({
                layoutType,
                subLayoutType,
                perCategorySubLayout,
                categoryOrder: orderData,
                hiddenCategories,
                subCategoryOrder: finalSubOrder,
                hiddenSubCategories,
                productCardStyle,
                navBarStyle,
                categoryPromoLabels,
            });

            setIsSaved(true);
            setHasChanges(false);
            setTimeout(() => setIsSaved(false), 3000);
        } catch (err) {
            console.error("Error saving category customization:", err);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3 mb-6"><MdCategory className="text-2xl text-blue-600" /><h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Category Customization</h2></div>
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div></div>
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
                <div className="flex items-center gap-3">
                    <MdCategory className="text-2xl text-blue-600" />
                    <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Category Customization</h2>
                    {isSaved && <span className="flex items-center text-green-600 text-sm font-medium ml-2"><FaCheckCircle className="w-4 h-4 mr-1" />Saved!</span>}
                </div>
                <button onClick={handleSave} disabled={!hasChanges || saving}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-white transition-all duration-200 shadow-sm ${hasChanges && !saving ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400 cursor-not-allowed'}`}>
                    <FiSave className="w-4 h-4" />{saving ? 'Saving...' : 'Save Changes'}
                </button>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 dark:border-gray-700 mb-6">
                <button onClick={() => setActiveTab('category')}
                    className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'category' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                    Category Layout
                </button>
                <button onClick={() => setActiveTab('subcategory')}
                    className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'subcategory' ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                    Sub-Category Layout
                </button>
                <button onClick={() => setActiveTab('productcard')}
                    className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === 'productcard' ? 'border-emerald-500 text-emerald-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                    <BsCreditCard2Back className="text-base" /> Product Card Style
                </button>
                <button onClick={() => setActiveTab('navbar')}
                    className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === 'navbar' ? 'border-cyan-500 text-cyan-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                    <BsPhone className="text-base" /> Navigation Bar
                </button>
                <button onClick={() => setActiveTab('hometheming')}
                    className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === 'hometheming' ? 'border-amber-500 text-amber-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                    <BsPalette className="text-base" /> Home Theming
                </button>
            </div>

            {activeTab === 'category' && (
                <>
                    {/* Layout Type Selection */}
                    <div className="mb-8">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wider">Select Category Layout Type</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                            {LAYOUT_OPTIONS.map((layout) => (
                                <button key={layout.value} onClick={() => handleLayoutChange(layout.value)}
                                    className={`relative p-0 rounded-2xl border-2 transition-all duration-200 text-left hover:shadow-md overflow-hidden ${layoutType === layout.value ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md ring-2 ring-blue-200' : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-300'}`}>
                                    {/* Mini Preview Header */}
                                    <div className="h-20 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 relative overflow-hidden">
                                        <div style={{ position: 'absolute', top: 0, left: 0, width: '200%', transform: 'scale(0.5)', transformOrigin: 'top left', pointerEvents: 'none' }}>
                                            <LayoutPreview layoutType={layout.value} categories={DUMMY_MINI_CATS} />
                                        </div>
                                        {layoutType === layout.value && (
                                            <div className="absolute top-2 right-2">
                                                <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-md">
                                                    <FaCheckCircle className="w-3.5 h-3.5 text-blue-500" />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {/* Card Body */}
                                    <div className="p-2.5 bg-white dark:bg-gray-700">
                                        <div className="flex items-center gap-1.5 mb-0.5">
                                            <span className="text-base">{layout.icon}</span>
                                            <span className="font-semibold text-xs text-gray-800 dark:text-gray-100">{layout.label}</span>
                                        </div>
                                        <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">{layout.desc}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Custom Promo Banner Labels */}
                    {LAYOUTS_WITH_PROMO_LABELS.includes(layoutType) && (
                        <div className="mb-8 p-4 rounded-2xl border border-pink-200 bg-gradient-to-br from-pink-50 to-sky-50 dark:from-pink-900/20 dark:to-sky-900/20 dark:border-pink-800">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Custom Promo Banner Labels</h3>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        Labels rotate on category tiles — e.g. &ldquo;Starting @ ₹99&rdquo;, &ldquo;Top Deals&rdquo;
                                    </p>
                                </div>
                                <button type="button" onClick={resetPromoLabels}
                                    className="text-xs font-medium text-pink-600 hover:text-pink-700 px-3 py-1.5 rounded-lg bg-white/80 border border-pink-200">
                                    Reset to defaults
                                </button>
                            </div>
                            <div className="space-y-2">
                                {categoryPromoLabels.map((label, index) => (
                                    <div key={index} className="flex items-center gap-2">
                                        <span className="text-xs text-gray-400 w-5 text-right flex-shrink-0">{index + 1}</span>
                                        <input
                                            type="text"
                                            value={label}
                                            onChange={(e) => updatePromoLabel(index, e.target.value)}
                                            placeholder="e.g. Starting @ ₹99"
                                            className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-pink-300 focus:border-pink-400 outline-none"
                                        />
                                        <button type="button" onClick={() => removePromoLabel(index)} disabled={categoryPromoLabels.length <= 1}
                                            className="p-2 rounded-lg text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                                            title="Remove label">
                                            ✕
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button type="button" onClick={addPromoLabel}
                                className="mt-3 text-xs font-semibold text-pink-600 hover:text-pink-700 flex items-center gap-1">
                                + Add promo label
                            </button>
                        </div>
                    )}

                    {/* Live Preview */}
                    <div className="mb-8">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wider">Live Preview</h3>
                        <div className="mx-auto max-w-xs">
                            <div className="border-2 border-gray-300 dark:border-gray-600 rounded-[2rem] p-2 bg-gray-50 dark:bg-gray-900 shadow-xl">
                                <div className="w-20 h-4 bg-gray-300 dark:bg-gray-700 rounded-full mx-auto mb-2" />
                                <div className="bg-white dark:bg-gray-800 rounded-2xl p-3 min-h-[160px]">
                                    <div className="flex items-center gap-1.5 mb-2">
                                        <div className="w-1 h-4 bg-blue-500 rounded-full" />
                                        <span className="font-bold text-xs text-gray-800 dark:text-gray-200">Shop by Category</span>
                                    </div>
                                    <LayoutPreview layoutType={layoutType} categories={visibleCategories} />
                                </div>
                                <div className="w-16 h-1 bg-gray-400 rounded-full mx-auto mt-2" />
                            </div>
                        </div>
                    </div>

                    {/* Category Order & Visibility */}
                    <div>
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wider">
                            Category Order & Visibility
                            <span className="ml-2 text-xs font-normal text-gray-400 normal-case">Drag to reorder | Click eye to hide | Expand for sub-categories</span>
                        </h3>
                        {categories.length === 0 ? (
                            <div className="text-center py-8 text-gray-500"><MdCategory className="text-4xl mx-auto mb-2 opacity-50" /><p className="text-sm">No categories found.</p></div>
                        ) : (
                            <div className="space-y-2">
                                {categories.map((cat, index) => {
                                    const isHidden = hiddenCategories.includes(String(cat._id));
                                    const subs = getSubCategories(cat._id);
                                    const hasSubs = subs.length > 0;
                                    const isExpanded = selectedCategoryForSubs === String(cat._id);
                                    const currentSubLayout = getCategorySubLayout(cat._id);
                                    return (
                                        <div key={cat._id} className={`rounded-lg border overflow-hidden transition-all duration-200 ${isExpanded ? 'border-purple-300 dark:border-purple-600 shadow-md ring-1 ring-purple-100' : 'border-gray-200 dark:border-gray-600'}`}>
                                            <div draggable onDragStart={(e) => handleDragStart(e, index)} onDragEnd={handleDragEnd} onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, index)}
                                                className={`flex items-center gap-3 p-3 transition-all duration-200 cursor-grab active:cursor-grabbing ${draggedIndex === index ? 'bg-blue-50 shadow-lg scale-[1.02]' : isHidden ? 'bg-gray-50 opacity-60' : 'bg-white dark:bg-gray-700 hover:shadow-md'}`}>
                                                <MdDragIndicator className="text-gray-400 text-lg flex-shrink-0" />
                                                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${isHidden ? 'bg-gray-200 text-gray-400' : 'bg-blue-100 text-blue-700'}`}>{index + 1}</span>
                                                <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                                                    {(cat.imageUrl || cat.preview) ? <img src={cat.imageUrl || cat.preview} alt={cat.categoryName} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-400"><MdCategory /></div>}
                                                </div>
                                                <span className={`font-medium text-sm flex-1 ${isHidden ? 'text-gray-400 line-through' : 'text-gray-800 dark:text-gray-200'}`}>{cat.categoryName}</span>
                                                {hasSubs && (
                                                    <button onClick={(e) => { e.stopPropagation(); setSelectedCategoryForSubs(isExpanded ? null : String(cat._id)); }}
                                                        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${isExpanded ? 'bg-purple-100 text-purple-700 ring-1 ring-purple-300' : 'bg-purple-50 text-purple-500 hover:bg-purple-100'}`}
                                                        title="Configure sub-categories">
                                                        <MdSubdirectoryArrowRight className="w-3.5 h-3.5" />
                                                        <span>{subs.length} subs</span>
                                                        {isExpanded ? <FiChevronUp className="w-3 h-3" /> : <FiChevronDown className="w-3 h-3" />}
                                                    </button>
                                                )}
                                                <div className="flex items-center gap-1">
                                                    <button onClick={() => moveCategory(index, -1)} disabled={index === 0} className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-30" title="Move up"><FiArrowUp className="w-4 h-4 text-gray-500" /></button>
                                                    <button onClick={() => moveCategory(index, 1)} disabled={index === categories.length - 1} className="p-1.5 rounded-md hover:bg-gray-100 disabled:opacity-30" title="Move down"><FiArrowDown className="w-4 h-4 text-gray-500" /></button>
                                                </div>
                                                <button onClick={() => toggleCategoryVisibility(cat._id)}
                                                    className={`p-2 rounded-lg transition-all ${isHidden ? 'bg-red-50 text-red-400 hover:bg-red-100' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}
                                                    title={isHidden ? 'Show' : 'Hide'}>
                                                    {isHidden ? <FiEyeOff className="w-4 h-4" /> : <FiEye className="w-4 h-4" />}
                                                </button>
                                            </div>

                                            {/* Expanded Sub-Category Panel */}
                                            {isExpanded && hasSubs && (
                                                <div className="border-t border-purple-100 dark:border-purple-800 bg-gradient-to-b from-purple-50/50 to-white dark:from-purple-900/10 dark:to-gray-800">
                                                    <div className="p-4 space-y-5">
                                                        {/* Per-Category Sub-Layout Selector */}
                                                        <div>
                                                            <h4 className="text-xs font-semibold text-purple-700 dark:text-purple-300 mb-2 uppercase tracking-wider flex items-center gap-1.5">
                                                                <MdSubdirectoryArrowRight className="text-sm" />
                                                                Sub-Category Layout for &ldquo;{cat.categoryName}&rdquo;
                                                                <span className="text-[10px] text-purple-400 font-normal normal-case ml-1">(Horizontally scrollable)</span>
                                                            </h4>
                                                            {perCategorySubLayout[String(cat._id)] && (
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <span className="text-[10px] text-purple-500 bg-purple-50 border border-purple-200 rounded-full px-2 py-0.5">Custom layout set for this category</span>
                                                                    <button onClick={() => { const next = { ...perCategorySubLayout }; delete next[String(cat._id)]; setPerCategorySubLayout(next); markChanged(); }}
                                                                        className="text-[10px] text-red-400 hover:text-red-600 underline">Reset to default</button>
                                                                </div>
                                                            )}
                                                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                                                                {SUB_LAYOUT_OPTIONS.map((layout) => (
                                                                    <button key={layout.value} onClick={() => handlePerCategorySubLayoutChange(cat._id, layout.value)}
                                                                        className={`relative p-2 rounded-lg border-2 transition-all duration-200 text-left hover:shadow-sm ${currentSubLayout === layout.value ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 shadow-sm ring-1 ring-purple-200' : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-300'}`}>
                                                                        {currentSubLayout === layout.value && <div className="absolute top-1.5 right-1.5"><FaCheckCircle className="w-3 h-3 text-purple-500" /></div>}
                                                                        <div className="flex items-center gap-1.5 mb-0.5">
                                                                            <span className="text-sm">{layout.icon}</span>
                                                                            <span className="font-medium text-xs text-gray-800 dark:text-gray-100">{layout.label}</span>
                                                                        </div>
                                                                        <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">{layout.desc}</p>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>

                                                        {/* Sub-Category Preview */}
                                                        <div>
                                                            <h4 className="text-xs font-semibold text-purple-700 dark:text-purple-300 mb-2 uppercase tracking-wider">Sub-Category Preview</h4>
                                                            <div className="mx-auto max-w-xs">
                                                                <div className="border-2 border-gray-300 dark:border-gray-600 rounded-[2rem] p-2 bg-gray-50 dark:bg-gray-900 shadow-lg">
                                                                    <div className="w-16 h-3 bg-gray-300 dark:bg-gray-700 rounded-full mx-auto mb-1.5" />
                                                                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-2.5 min-h-[50px]">
                                                                        <div className="flex items-center gap-1 mb-1.5">
                                                                            <MdSubdirectoryArrowRight className="text-purple-500 text-[10px]" />
                                                                            <span className="font-bold text-[9px] text-gray-600 dark:text-gray-300">{cat.categoryName} — Sub-categories</span>
                                                                        </div>
                                                                        <SubLayoutPreview layoutType={currentSubLayout} items={getVisibleSubCategories(cat._id)} />
                                                                    </div>
                                                                    <div className="w-12 h-1 bg-gray-400 rounded-full mx-auto mt-1.5" />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Sub-Category Order & Visibility */}
                                                        <div>
                                                            <h4 className="text-xs font-semibold text-purple-700 dark:text-purple-300 mb-2 uppercase tracking-wider">
                                                                Sub-Category Order & Visibility
                                                                <span className="ml-1.5 text-[10px] font-normal text-purple-400 normal-case">Drag to reorder | Click eye to hide</span>
                                                            </h4>
                                                            <div className="space-y-1.5">
                                                                {subs.map((sub, idx) => {
                                                                    const isSubHidden = hiddenSubCategories.includes(String(sub._id));
                                                                    return (
                                                                        <div key={sub._id} draggable
                                                                            onDragStart={(e) => handleSubDragStart(e, idx)}
                                                                            onDragEnd={handleSubDragEnd}
                                                                            onDragOver={handleDragOver}
                                                                            onDrop={(e) => handleSubDrop(e, cat._id, idx)}
                                                                            className={`flex items-center gap-2 p-2 rounded-md border transition-all cursor-grab active:cursor-grabbing ${subDraggedIndex === idx ? 'border-purple-400 bg-purple-50 shadow-md' : isSubHidden ? 'border-gray-100 bg-gray-50 opacity-50' : 'border-gray-100 bg-white dark:bg-gray-700 hover:bg-gray-50'}`}>
                                                                            <MdDragIndicator className="text-gray-300 text-sm flex-shrink-0" />
                                                                            <MdSubdirectoryArrowRight className="text-gray-400 text-sm flex-shrink-0" />
                                                                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${isSubHidden ? 'bg-gray-200 text-gray-400' : 'bg-purple-100 text-purple-700'}`}>{idx + 1}</span>
                                                                            <div className="w-7 h-7 rounded overflow-hidden bg-gray-200 flex-shrink-0">
                                                                                {(sub.imageUrl || sub.preview) ? <img src={sub.imageUrl || sub.preview} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-gray-200" />}
                                                                            </div>
                                                                            <span className={`text-xs flex-1 ${isSubHidden ? 'text-gray-400 line-through' : 'text-gray-700 dark:text-gray-300'}`}>{sub.categoryName}</span>
                                                                            <div className="flex items-center gap-0.5">
                                                                                <button onClick={() => moveSubCategory(cat._id, idx, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><FiArrowUp className="w-3 h-3 text-gray-400" /></button>
                                                                                <button onClick={() => moveSubCategory(cat._id, idx, 1)} disabled={idx === subs.length - 1} className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><FiArrowDown className="w-3 h-3 text-gray-400" /></button>
                                                                            </div>
                                                                            <button onClick={() => toggleSubCategoryVisibility(sub._id)}
                                                                                className={`p-1.5 rounded-md transition-all ${isSubHidden ? 'bg-red-50 text-red-400 hover:bg-red-100' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}>
                                                                                {isSubHidden ? <FiEyeOff className="w-3 h-3" /> : <FiEye className="w-3 h-3" />}
                                                                            </button>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                            <div className="flex gap-3 mt-2 text-[10px] text-gray-500">
                                                                <span>Total: <b className="text-gray-700">{subs.length}</b></span>
                                                                <span>Visible: <b className="text-green-600">{subs.filter(s => !hiddenSubCategories.includes(String(s._id))).length}</b></span>
                                                                <span>Hidden: <b className="text-red-500">{subs.filter(s => hiddenSubCategories.includes(String(s._id))).length}</b></span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <div className="flex gap-4 mt-4 text-xs text-gray-500">
                            <span>Total: <b className="text-gray-700">{categories.length}</b></span>
                            <span>Visible: <b className="text-green-600">{visibleCategories.length}</b></span>
                            <span>Hidden: <b className="text-red-500">{hiddenCategories.length}</b></span>
                        </div>
                    </div>
                </>
            )}

            {activeTab === 'subcategory' && (
                <>
                    {/* Info Banner */}
                    <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-lg px-4 py-3 mb-6">
                        <p className="text-xs text-purple-700 dark:text-purple-300"><b>Default Layout:</b> This layout applies to all categories that don't have a specific sub-category layout override. You can set per-category sub-layouts by expanding a category in the <b>Category Layout</b> tab → <b>Category Order & Visibility</b> section.</p>
                    </div>

                    {/* Default Sub-Category Layout Type Selection */}
                    <div className="mb-8">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wider">Default Sub-Category Layout Type <span className="text-[10px] text-purple-500 font-normal normal-case ml-1">(All horizontally scrollable)</span></h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
                            {SUB_LAYOUT_OPTIONS.map((layout) => (
                                <button key={layout.value} onClick={() => handleSubLayoutChange(layout.value)}
                                    className={`relative p-0 rounded-2xl border-2 transition-all duration-200 text-left hover:shadow-md overflow-hidden ${subLayoutType === layout.value ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30 shadow-md ring-2 ring-purple-200' : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-300'}`}>
                                    {/* Mini Preview Header */}
                                    <div className="h-14 bg-gradient-to-b from-purple-50 to-purple-100 dark:from-gray-700 dark:to-gray-800 relative overflow-hidden">
                                        <div style={{ position: 'absolute', top: 6, left: 0, width: '200%', transform: 'scale(0.5)', transformOrigin: 'top left', pointerEvents: 'none' }}>
                                            <SubLayoutPreview layoutType={layout.value} items={DUMMY_MINI_SUBS} />
                                        </div>
                                        {subLayoutType === layout.value && (
                                            <div className="absolute top-1.5 right-1.5">
                                                <div className="w-5 h-5 bg-white rounded-full flex items-center justify-center shadow-md">
                                                    <FaCheckCircle className="w-3.5 h-3.5 text-purple-500" />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {/* Card Body */}
                                    <div className="p-2.5 bg-white dark:bg-gray-700">
                                        <div className="flex items-center gap-1.5 mb-0.5">
                                            <span className="text-base">{layout.icon}</span>
                                            <span className="font-semibold text-xs text-gray-800 dark:text-gray-100">{layout.label}</span>
                                        </div>
                                        <p className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">{layout.desc}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Default Sub-Category Preview */}
                    <div className="mb-8">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wider">Default Sub-Category Preview</h3>
                        <div className="mx-auto max-w-xs">
                            <div className="border-2 border-gray-300 dark:border-gray-600 rounded-[2rem] p-2 bg-gray-50 dark:bg-gray-900 shadow-xl">
                                <div className="w-20 h-4 bg-gray-300 dark:bg-gray-700 rounded-full mx-auto mb-2" />
                                <div className="bg-white dark:bg-gray-800 rounded-2xl p-3 min-h-[60px]">
                                    <div className="flex items-center gap-1.5 mb-2">
                                        <MdSubdirectoryArrowRight className="text-purple-500 text-xs" />
                                        <span className="font-bold text-[10px] text-gray-600 dark:text-gray-300">Sub-categories (Default)</span>
                                    </div>
                                    <SubLayoutPreview layoutType={subLayoutType} items={
                                        (() => {
                                            const catWithSubs = categories.find(c => getSubCategories(c._id).length > 0);
                                            return catWithSubs ? getVisibleSubCategories(catWithSubs._id) : [];
                                        })()
                                    } />
                                </div>
                                <div className="w-16 h-1 bg-gray-400 rounded-full mx-auto mt-2" />
                            </div>
                        </div>
                    </div>

                    {/* Per-Category Override Status */}
                    <div>
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wider">Per-Category Layout Overrides</h3>
                        {Object.keys(perCategorySubLayout).length === 0 ? (
                            <div className="text-center py-6 text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-dashed border-gray-300 dark:border-gray-600">
                                <MdSubdirectoryArrowRight className="text-3xl mx-auto mb-1 opacity-40" />
                                <p className="text-xs">No per-category overrides set.</p>
                                <p className="text-[10px] text-gray-400 mt-0.5">All categories use the default layout above.</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {categories.filter(cat => perCategorySubLayout[String(cat._id)]).map((cat) => {
                                    const layout = SUB_LAYOUT_OPTIONS.find(l => l.value === perCategorySubLayout[String(cat._id)]);
                                    return (
                                        <div key={cat._id} className="flex items-center gap-3 p-3 rounded-lg border border-purple-200 bg-purple-50/50 dark:bg-purple-900/20 dark:border-purple-700">
                                            <div className="w-8 h-8 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                                                {(cat.imageUrl || cat.preview) ? <img src={cat.imageUrl || cat.preview} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-gray-400"><MdCategory /></div>}
                                            </div>
                                            <span className="font-medium text-sm text-gray-800 dark:text-gray-200 flex-1">{cat.categoryName}</span>
                                            <span className="text-xs text-purple-600 font-medium">{layout?.icon} {layout?.label || perCategorySubLayout[String(cat._id)]}</span>
                                            <button onClick={() => { const next = { ...perCategorySubLayout }; delete next[String(cat._id)]; setPerCategorySubLayout(next); markChanged(); }}
                                                className="text-xs text-red-400 hover:text-red-600 px-2 py-1 rounded hover:bg-red-50 transition-colors" title="Remove override and use default">
                                                Reset
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* ========== PRODUCT CARD STYLE TAB ========== */}
            {activeTab === 'productcard' && (
                <>
                    <div className="mb-6">
                        <div className="flex items-center gap-2 mb-1">
                            <BsCreditCard2Back className="text-emerald-500 text-lg" />
                            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Dynamic Product Card Style</h3>
                        </div>
                        <p className="text-xs text-gray-400 mb-4">Choose how product cards appear across your entire store. This style will be applied everywhere products are displayed.</p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Card Style Selector */}
                        <div className="lg:col-span-2">
                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                                {CARD_STYLE_OPTIONS.map((card) => (
                                    <button key={card.value} onClick={() => handleProductCardStyleChange(card.value)}
                                        className={`relative p-0 rounded-2xl border-2 transition-all duration-200 text-left hover:shadow-lg overflow-hidden ${productCardStyle === card.value
                                            ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 shadow-lg ring-2 ring-emerald-200'
                                            : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-300'}`}>
                                        {/* Mini Preview Header */}
                                        <div className="h-24 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 relative overflow-hidden flex items-start justify-center">
                                            <div style={{ transform: 'scale(0.4)', transformOrigin: 'top center', pointerEvents: 'none', marginTop: -4 }}>
                                                <CardStylePreview style={card.value} />
                                            </div>
                                            {productCardStyle === card.value && (
                                                <div className="absolute top-2 right-2">
                                                    <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md">
                                                        <FaCheckCircle className="w-4 h-4 text-emerald-500" />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        {/* Card Body */}
                                        <div className="p-3 bg-white dark:bg-gray-700">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-xl">{card.icon}</span>
                                                <span className="font-bold text-sm text-gray-800 dark:text-gray-100">{card.label}</span>
                                            </div>
                                            <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">{card.desc}</p>
                                        </div>
                                    </button>
                                ))}
                            </div>

                            {/* Currently Selected Indicator */}
                            <div className="mt-6 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-800 flex items-center justify-center text-xl">
                                        {CARD_STYLE_OPTIONS.find(c => c.value === productCardStyle)?.icon || '🏪'}
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-emerald-800 dark:text-emerald-200">
                                            Active: {CARD_STYLE_OPTIONS.find(c => c.value === productCardStyle)?.label || 'Classic Grid'}
                                        </p>
                                        <p className="text-xs text-emerald-600 dark:text-emerald-400">
                                            {CARD_STYLE_OPTIONS.find(c => c.value === productCardStyle)?.desc}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Live Preview */}
                        <div className="lg:col-span-1">
                            <div className="sticky top-6">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 text-center">Live Preview</h4>
                                <CardStylePreview style={productCardStyle} />
                                <p className="text-[10px] text-gray-400 text-center mt-3">This is how product cards will appear in your store app</p>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ========== NAVIGATION BAR TAB ========== */}
            {activeTab === 'navbar' && (
                <>
                    <div className="mb-6">
                        <div className="flex items-center gap-2 mb-1">
                            <BsPhone className="text-cyan-500 text-lg" />
                            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Navigation Bar Style</h3>
                        </div>
                        <p className="text-xs text-gray-400 mb-4">Choose a beautiful bottom navigation bar design for your mobile app. This controls the look and feel of the tab bar shown at the bottom of every screen.</p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Style Cards */}
                        <div className="lg:col-span-2">
                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                                {NAVBAR_STYLE_OPTIONS.map((style) => (
                                    <button key={style.value} onClick={() => handleNavBarStyleChange(style.value)}
                                        className={`relative p-0 rounded-2xl border-2 transition-all duration-300 text-left hover:shadow-xl overflow-hidden group ${
                                            navBarStyle === style.value
                                                ? 'border-cyan-500 shadow-xl ring-2 ring-cyan-200 scale-[1.02]'
                                                : 'border-gray-200 dark:border-gray-600 hover:border-cyan-300'
                                        }`}>
                                        {/* Mini Preview Header */}
                                        <div className="h-24 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 relative overflow-hidden flex items-end justify-center pb-1">
                                            <NavBarMiniPreview style={style.value} accent={style.preview.accent} />
                                            {navBarStyle === style.value && (
                                                <div className="absolute top-2 right-2">
                                                    <div className="w-7 h-7 bg-white rounded-full flex items-center justify-center shadow-lg">
                                                        <FaCheckCircle className="w-5 h-5 text-cyan-500" />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        {/* Card Body */}
                                        <div className="p-3 bg-white dark:bg-gray-700">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="text-lg">{style.icon}</span>
                                                <h4 className="font-bold text-sm text-gray-800 dark:text-gray-100">{style.label}</h4>
                                            </div>
                                            <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">{style.desc}</p>
                                        </div>
                                    </button>
                                ))}
                            </div>

                            {/* Currently Selected Indicator */}
                            <div className="mt-6 p-4 rounded-xl bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-700">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-lg bg-cyan-100 dark:bg-cyan-800 flex items-center justify-center text-2xl">
                                        {NAVBAR_STYLE_OPTIONS.find(s => s.value === navBarStyle)?.icon || '💊'}
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-cyan-800 dark:text-cyan-200">
                                            Active: {NAVBAR_STYLE_OPTIONS.find(s => s.value === navBarStyle)?.label || 'Floating Pill'}
                                        </p>
                                        <p className="text-xs text-cyan-600 dark:text-cyan-400">
                                            {NAVBAR_STYLE_OPTIONS.find(s => s.value === navBarStyle)?.desc}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Live Phone Preview */}
                        <div className="lg:col-span-1">
                            <div className="sticky top-6">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 text-center">Live Preview</h4>
                                <NavBarStylePreview style={navBarStyle} />
                                <p className="text-[10px] text-gray-400 text-center mt-3">This is how the navigation bar will appear in your mobile app</p>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* ========== HOME THEMING TAB ========== */}
            {activeTab === 'hometheming' && (
                <>
                    <div className="mb-6">
                        <div className="flex items-center gap-2 mb-1">
                            <BsPalette className="text-amber-500 text-lg" />
                            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Home Page Theming</h3>
                        </div>
                        <p className="text-xs text-gray-400 mb-4">Select a seasonal or festival theme for your app's home page. Themes apply animated effects to banners, categories, header, and section backgrounds.</p>
                        {homeThemeSaving && <p className="text-xs text-amber-500 mb-2 animate-pulse">Saving theme...</p>}
                    </div>

                    {/* Category Filter Pills */}
                    <div className="flex flex-wrap gap-2 mb-6">
                        {['All', ...HOME_THEME_CATEGORIES].map(cat => (
                            <button key={cat} onClick={() => setThemeFilterCategory(cat)}
                                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${themeFilterCategory === cat ? 'bg-amber-500 text-white shadow-md' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200'}`}>
                                {cat}
                            </button>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Theme Cards Grid */}
                        <div className="lg:col-span-2">
                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                                {HOME_THEME_OPTIONS
                                    .filter(t => themeFilterCategory === 'All' || t.category === themeFilterCategory)
                                    .map((theme) => (
                                    <button key={theme.value} onClick={() => handleHomeThemeSave(theme.value)}
                                        className={`relative p-0 rounded-2xl border-2 transition-all duration-300 text-left hover:shadow-xl overflow-hidden group ${homeThemeId === theme.value
                                            ? 'border-amber-500 shadow-xl ring-2 ring-amber-200 scale-[1.02]'
                                            : 'border-gray-200 dark:border-gray-600 hover:border-amber-300'}`}>
                                        {/* Gradient Preview Header */}
                                        <div className={`h-20 bg-gradient-to-r ${theme.gradient} relative overflow-hidden`}>
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                                            <span className="absolute top-3 left-3 text-3xl drop-shadow-lg">{theme.icon}</span>
                                            {homeThemeId === theme.value && (
                                                <div className="absolute top-2 right-2">
                                                    <div className="w-7 h-7 bg-white rounded-full flex items-center justify-center shadow-lg">
                                                        <FaCheckCircle className="w-5 h-5 text-amber-500" />
                                                    </div>
                                                </div>
                                            )}
                                            <div className="absolute bottom-2 right-3">
                                                <span className="text-[10px] bg-white/30 backdrop-blur-sm text-white px-2 py-0.5 rounded-full font-semibold">
                                                    {theme.category}
                                                </span>
                                            </div>
                                        </div>
                                        {/* Card Body */}
                                        <div className="p-3 bg-white dark:bg-gray-700">
                                            <h4 className="font-bold text-sm text-gray-800 dark:text-gray-100 mb-0.5">{theme.label}</h4>
                                            <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">{theme.desc}</p>
                                        </div>
                                    </button>
                                ))}
                            </div>

                            {/* Currently Selected Indicator */}
                            <div className="mt-6 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-lg bg-amber-100 dark:bg-amber-800 flex items-center justify-center text-2xl">
                                        {HOME_THEME_OPTIONS.find(t => t.value === homeThemeId)?.icon || '⚪'}
                                    </div>
                                    <div>
                                        <p className="text-sm font-bold text-amber-800 dark:text-amber-200">
                                            Active Theme: {HOME_THEME_OPTIONS.find(t => t.value === homeThemeId)?.label || 'No Theme'}
                                        </p>
                                        <p className="text-xs text-amber-600 dark:text-amber-400">
                                            {HOME_THEME_OPTIONS.find(t => t.value === homeThemeId)?.desc || 'No seasonal theme applied'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Theme Preview - Phone Mockup */}
                        <div className="lg:col-span-1">
                            <div className="sticky top-6">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 text-center">Theme Preview</h4>
                                <HomeThemePreview themeId={homeThemeId} />
                                <p className="text-[10px] text-gray-400 text-center mt-3">Preview of how your app home page will look with the selected theme</p>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

// =============================================
// NAVIGATION BAR MINI PREVIEW (inside card)
// =============================================
const NavBarMiniPreview = ({ style, accent }) => {
    const tabs = ['🏠', '💖', '🛒', '📦', '👤'];
    const activeIdx = 0;
    const a = accent || '#6366f1';

    const baseBar = { display: 'flex', alignItems: 'center', justifyContent: 'space-around', width: '92%', fontSize: 10, padding: '4px 2px' };

    const renderIcon = (icon, i) => {
        const isActive = i === activeIdx;
        const inactiveColor = '#9ca3af';

        switch (style) {
            case 'floating-pill':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {isActive ? (
                            <div style={{ width: 22, height: 22, borderRadius: 11, background: `linear-gradient(135deg, ${a}, ${a}cc)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, boxShadow: `0 2px 8px ${a}66` }}>{icon}</div>
                        ) : (
                            <span style={{ fontSize: 9, opacity: 0.5 }}>{icon}</span>
                        )}
                    </div>
                );
            case 'glass-dock':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {isActive ? (
                            <div style={{ width: 22, height: 22, borderRadius: 11, background: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9 }}>{icon}</div>
                        ) : (
                            <span style={{ fontSize: 9, opacity: 0.4 }}>{icon}</span>
                        )}
                    </div>
                );
            case 'minimal-flat':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <span style={{ fontSize: 9, color: isActive ? a : inactiveColor }}>{icon}</span>
                        {isActive && <div style={{ width: 3, height: 3, borderRadius: '50%', background: a }} />}
                    </div>
                );
            case 'curved-notch':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {i === 2 ? (
                            <div style={{ width: 26, height: 26, borderRadius: 13, background: `linear-gradient(135deg, ${a}, ${a}bb)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, marginTop: -10, border: '2px solid #fff', boxShadow: `0 3px 10px ${a}44` }}>{icon}</div>
                        ) : (
                            <span style={{ fontSize: 9, color: isActive ? a : inactiveColor }}>{icon}</span>
                        )}
                    </div>
                );
            case 'neon-glow':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {isActive ? (
                            <div style={{ width: 22, height: 22, borderRadius: 11, border: `1.5px solid ${a}`, boxShadow: `0 0 8px ${a}88, inset 0 0 4px ${a}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9 }}>{icon}</div>
                        ) : (
                            <span style={{ fontSize: 9, opacity: 0.35 }}>{icon}</span>
                        )}
                    </div>
                );
            case 'bubble-pop':
                return (
                    <div key={i}>
                        {isActive ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 2, background: `${a}20`, borderRadius: 12, padding: '2px 6px 2px 3px' }}>
                                <div style={{ width: 18, height: 18, borderRadius: 9, background: a, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, color: '#fff' }}>{icon}</div>
                                <span style={{ fontSize: 6, fontWeight: 700, color: a }}>Home</span>
                            </div>
                        ) : (
                            <span style={{ fontSize: 9, opacity: 0.5 }}>{icon}</span>
                        )}
                    </div>
                );
            case 'elegant-line':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {isActive && <div style={{ width: 14, height: 2, borderRadius: 1, background: a, marginBottom: 2 }} />}
                        <span style={{ fontSize: 9, color: isActive ? a : inactiveColor }}>{icon}</span>
                    </div>
                );
            case 'island-float':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ width: 22, height: 22, borderRadius: 8, background: isActive ? `${a}18` : '#f9fafb', border: isActive ? `1.5px solid ${a}44` : '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9 }}>{icon}</div>
                    </div>
                );
            case 'neumorphic-soft':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ width: 22, height: 22, borderRadius: 11, background: '#e5e7eb', boxShadow: isActive ? `inset 2px 2px 4px #b8b9be, inset -2px -2px 4px #fff` : '2px 2px 4px #b8b9be, -2px -2px 4px #fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9 }}>{icon}</div>
                    </div>
                );
            case 'gradient-wave':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <span style={{ fontSize: 9, color: '#fff', opacity: isActive ? 1 : 0.5 }}>{icon}</span>
                        {isActive && <div style={{ width: 4, height: 4, borderRadius: 2, background: '#fff', marginTop: 1 }} />}
                    </div>
                );
            case 'material-you':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {isActive ? (
                            <div style={{ background: `${a}22`, borderRadius: 10, padding: '3px 8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <span style={{ fontSize: 9, color: a }}>{icon}</span>
                            </div>
                        ) : (
                            <span style={{ fontSize: 9, opacity: 0.5 }}>{icon}</span>
                        )}
                    </div>
                );
            case 'retro-arcade':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ width: 22, height: 22, borderRadius: 3, background: isActive ? a : '#2a2a4a', border: isActive ? `2px solid ${a}` : '1px solid #3a3a5a', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9 }}>{icon}</div>
                    </div>
                );
            default:
                return <span key={i} style={{ fontSize: 9, color: isActive ? a : inactiveColor }}>{icon}</span>;
        }
    };

    const barBg = style === 'neon-glow' || style === 'retro-arcade' ? '#0f172a' : style === 'gradient-wave' ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : '#ffffff';
    const barRadius = style === 'floating-pill' || style === 'bubble-pop' ? 14 : style === 'glass-dock' ? 12 : style === 'neon-glow' ? 10 : style === 'neumorphic-soft' ? 12 : style === 'retro-arcade' ? 2 : 0;

    return (
        <div style={{
            ...baseBar,
            background: barBg,
            borderRadius: barRadius,
            height: 28,
            ...(style === 'minimal-flat' || style === 'elegant-line' ? { borderTop: '1px solid #e5e7eb' } : {}),
            ...(style === 'glass-dock' ? { border: '1px solid rgba(255,255,255,0.4)', backdropFilter: 'blur(8px)' } : {}),
            ...(style === 'floating-pill' || style === 'bubble-pop' || style === 'neon-glow' || style === 'glass-dock' ? { boxShadow: '0 -2px 12px rgba(0,0,0,0.08)' } : {}),
        }}>
            {tabs.map((icon, i) => renderIcon(icon, i))}
        </div>
    );
};

// =============================================
// NAVIGATION BAR PHONE PREVIEW (large)
// =============================================
const NavBarStylePreview = ({ style }) => {
    const opt = NAVBAR_STYLE_OPTIONS.find(s => s.value === style) || NAVBAR_STYLE_OPTIONS[0];
    const a = opt.preview.accent;
    const tabs = [
        { icon: '🏠', label: 'Home' },
        { icon: '💖', label: 'Favorites' },
        { icon: '🛒', label: 'Cart' },
        { icon: '📦', label: 'Orders' },
        { icon: '👤', label: 'Profile' },
    ];

    const renderTab = (tab, i) => {
        const isActive = i === 0;
        const inactiveColor = '#9ca3af';

        const iconEl = (fontSize = 14) => (
            <span style={{ fontSize, lineHeight: 1 }}>{tab.icon}</span>
        );

        const labelEl = (color, fw = 600) => (
            <span style={{ fontSize: 7, fontWeight: fw, color, marginTop: 1, letterSpacing: -0.2 }}>{tab.label}</span>
        );

        switch (style) {
            case 'floating-pill':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        {isActive ? (
                            <div style={{ width: 36, height: 36, borderRadius: 18, background: `linear-gradient(135deg, ${a}, ${a}cc)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 14px ${a}55` }}>
                                <div style={{ width: 32, height: 32, borderRadius: 16, background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{iconEl(13)}</div>
                            </div>
                        ) : (
                            <div style={{ width: 34, height: 34, borderRadius: 17, background: '#E5E4E2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{iconEl(13)}</div>
                        )}
                        {labelEl(isActive ? a : inactiveColor, isActive ? 700 : 500)}
                    </div>
                );
            case 'glass-dock':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        {isActive ? (
                            <div style={{ width: 36, height: 36, borderRadius: 18, background: 'rgba(255,255,255,0.55)', border: '1.5px solid rgba(255,255,255,0.8)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 2px 10px ${a}33` }}>{iconEl(13)}</div>
                        ) : (
                            <div style={{ width: 34, height: 34, borderRadius: 17, background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{iconEl(13)}</div>
                        )}
                        {labelEl(isActive ? a : 'rgba(255,255,255,0.6)', isActive ? 700 : 500)}
                    </div>
                );
            case 'minimal-flat':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, paddingTop: 4 }}>
                        {iconEl(15)}
                        {labelEl(isActive ? a : inactiveColor, isActive ? 700 : 400)}
                        {isActive && <div style={{ width: 4, height: 4, borderRadius: 2, background: a, marginTop: -1 }} />}
                    </div>
                );
            case 'curved-notch':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        {i === 2 ? (
                            <>
                                <div style={{ width: 42, height: 42, borderRadius: 21, background: `linear-gradient(135deg, ${a}, ${a}bb)`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: -20, border: '3px solid #fff', boxShadow: `0 4px 16px ${a}44` }}>{iconEl(16)}</div>
                                {labelEl(a, 700)}
                            </>
                        ) : (
                            <>
                                {iconEl(14)}
                                {labelEl(isActive ? a : inactiveColor)}
                            </>
                        )}
                    </div>
                );
            case 'neon-glow':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        {isActive ? (
                            <div style={{ width: 36, height: 36, borderRadius: 18, border: `2px solid ${a}`, boxShadow: `0 0 12px ${a}88, inset 0 0 6px ${a}44`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{iconEl(13)}</div>
                        ) : (
                            <div style={{ opacity: 0.35 }}>{iconEl(14)}</div>
                        )}
                        {labelEl(isActive ? a : '#475569', isActive ? 700 : 400)}
                    </div>
                );
            case 'bubble-pop':
                return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                        {isActive ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: `${a}18`, borderRadius: 20, padding: '5px 10px' }}>
                                <div style={{ width: 28, height: 28, borderRadius: 14, background: a, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 2px 8px ${a}55` }}>{iconEl(11)}</div>
                                <span style={{ fontSize: 9, fontWeight: 800, color: a }}>{tab.label}</span>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0 6px' }}>
                                {iconEl(14)}
                            </div>
                        )}
                    </div>
                );
            case 'elegant-line':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        {isActive && <div style={{ width: 20, height: 2.5, borderRadius: 1.5, background: a, marginBottom: 2 }} />}
                        {iconEl(14)}
                        {labelEl(isActive ? a : inactiveColor, isActive ? 700 : 400)}
                    </div>
                );
            case 'island-float':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 12, background: isActive ? `${a}12` : '#f9fafb', border: isActive ? `2px solid ${a}44` : '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: isActive ? `0 2px 8px ${a}22` : 'none' }}>{iconEl(13)}</div>
                        {labelEl(isActive ? a : inactiveColor, isActive ? 700 : 400)}
                    </div>
                );
            case 'neumorphic-soft':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 18, background: '#e5e7eb', boxShadow: isActive ? 'inset 3px 3px 6px #b8b9be, inset -3px -3px 6px #fff' : '3px 3px 6px #b8b9be, -3px -3px 6px #fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{iconEl(13)}</div>
                        {labelEl(isActive ? a : inactiveColor, isActive ? 700 : 400)}
                    </div>
                );
            case 'gradient-wave':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <span style={{ fontSize: 14, color: '#fff', opacity: isActive ? 1 : 0.45 }}>{tab.icon}</span>
                        {labelEl(isActive ? '#fff' : 'rgba(255,255,255,0.45)', isActive ? 700 : 400)}
                        {isActive && <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#fff', marginTop: -1 }} />}
                    </div>
                );
            case 'material-you':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        {isActive ? (
                            <div style={{ background: `${a}22`, borderRadius: 14, padding: '4px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{iconEl(14)}</div>
                        ) : (
                            iconEl(14)
                        )}
                        {labelEl(isActive ? a : inactiveColor, isActive ? 700 : 500)}
                    </div>
                );
            case 'retro-arcade':
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <div style={{ width: 36, height: 32, borderRadius: 4, background: isActive ? a : '#2a2a4a', border: isActive ? `2px solid ${a}` : '1.5px solid #3a3a5a', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: isActive ? `0 0 8px ${a}66` : 'none' }}>{iconEl(13)}</div>
                        {labelEl(isActive ? a : '#64748b', isActive ? 800 : 400)}
                    </div>
                );
            default:
                return (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        {iconEl(14)}
                        {labelEl(isActive ? a : inactiveColor)}
                    </div>
                );
        }
    };

    // Bar background
    const getBarStyle = () => {
        const base = { position: 'absolute', bottom: 0, left: 0, right: 0, display: 'flex', justifyContent: 'space-around', alignItems: 'center', paddingTop: 6, paddingBottom: 8 };
        switch (style) {
            case 'floating-pill':
                return { ...base, bottom: 6, left: 10, right: 10, background: '#fff', borderRadius: 28, height: 64, boxShadow: '0 -4px 24px rgba(0,0,0,0.12)', paddingTop: 8, paddingBottom: 10 };
            case 'glass-dock':
                return { ...base, bottom: 6, left: 10, right: 10, background: 'rgba(255,255,255,0.65)', borderRadius: 24, height: 62, backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.5)', boxShadow: '0 -2px 20px rgba(0,0,0,0.06)' };
            case 'minimal-flat':
                return { ...base, background: '#fff', borderTop: '1px solid #e5e7eb', height: 56 };
            case 'curved-notch':
                return { ...base, background: '#fff', height: 58, boxShadow: '0 -2px 12px rgba(0,0,0,0.06)', borderTopLeftRadius: 20, borderTopRightRadius: 20 };
            case 'neon-glow':
                return { ...base, bottom: 6, left: 8, right: 8, background: '#0f172a', borderRadius: 20, height: 62, boxShadow: `0 0 20px ${a}33, 0 -2px 12px rgba(0,0,0,0.3)`, border: `1px solid ${a}33` };
            case 'bubble-pop':
                return { ...base, bottom: 6, left: 10, right: 10, background: '#fff', borderRadius: 28, height: 56, boxShadow: '0 -4px 24px rgba(0,0,0,0.1)' };
            case 'elegant-line':
                return { ...base, background: '#fff', borderTop: `2px solid ${a}22`, height: 58 };
            case 'island-float':
                return { ...base, bottom: 6, left: 8, right: 8, background: '#f1f5f9', borderRadius: 16, height: 62, boxShadow: '0 -2px 16px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' };
            case 'neumorphic-soft':
                return { ...base, bottom: 6, left: 8, right: 8, background: '#e5e7eb', borderRadius: 24, height: 62, boxShadow: '6px 6px 12px #b8b9be, -6px -6px 12px #ffffff' };
            case 'gradient-wave':
                return { ...base, background: `linear-gradient(135deg, ${a === '#ffffff' ? '#6366f1' : a}, #8b5cf6)`, height: 60, boxShadow: '0 -4px 16px rgba(99,102,241,0.3)' };
            case 'material-you':
                return { ...base, background: '#fff', height: 60, boxShadow: '0 -1px 6px rgba(0,0,0,0.04)' };
            case 'retro-arcade':
                return { ...base, bottom: 4, left: 6, right: 6, background: '#1a1a2e', borderRadius: 6, height: 58, border: '2px solid #3a3a5a', boxShadow: '0 0 12px rgba(251,191,36,0.15)' };
            default:
                return { ...base, background: '#fff', height: 56 };
        }
    };

    return (
        <div className="relative mx-auto" style={{ width: 220, height: 420, borderRadius: 30, border: '8px solid #1f2937', backgroundColor: '#f9fafb', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            {/* Status Bar */}
            <div style={{ height: 24, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px', fontSize: 8, color: '#374151', fontWeight: 700, borderBottom: '1px solid #f3f4f6' }}>
                <span>9:41</span>
                <div style={{ display: 'flex', gap: 3 }}><span>📶</span><span>🔋</span></div>
            </div>
            {/* Header */}
            <div style={{ height: 44, background: '#fff', display: 'flex', alignItems: 'center', padding: '0 10px', gap: 6, borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ width: 24, height: 24, borderRadius: 12, background: `${opt.preview.accent}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>🏪</div>
                <div style={{ flex: 1, height: 24, borderRadius: 12, background: '#f3f4f6', display: 'flex', alignItems: 'center', paddingLeft: 8, fontSize: 7, color: '#9ca3af' }}>🔍 Search products...</div>
            </div>
            {/* Content area */}
            <div style={{ padding: '10px 8px' }}>
                {/* Categories */}
                <div style={{ fontSize: 8, fontWeight: 700, color: '#374151', marginBottom: 6 }}>Shop by Category</div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} style={{ textAlign: 'center' }}>
                            <div style={{ width: 32, height: 32, borderRadius: 16, background: '#e5e7eb' }} />
                            <div style={{ fontSize: 5, color: '#9ca3af', marginTop: 2 }}>Cat {i}</div>
                        </div>
                    ))}
                </div>
                {/* Products */}
                <div style={{ fontSize: 8, fontWeight: 700, color: '#374151', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 2, height: 10, borderRadius: 1, background: opt.preview.accent }} />
                    Featured
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                    {[1, 2].map(i => (
                        <div key={i} style={{ flex: 1, borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb', background: '#fff' }}>
                            <div style={{ height: 60, background: i === 1 ? '#dbeafe' : '#fce7f3' }} />
                            <div style={{ padding: 4 }}>
                                <div style={{ height: 4, width: '80%', background: '#e5e7eb', borderRadius: 2, marginBottom: 3 }} />
                                <div style={{ height: 4, width: '50%', background: '#d1d5db', borderRadius: 2 }} />
                            </div>
                        </div>
                    ))}
                </div>
                <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                    {[3, 4].map(i => (
                        <div key={i} style={{ flex: 1, borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb', background: '#fff' }}>
                            <div style={{ height: 60, background: i === 3 ? '#d1fae5' : '#fef3c7' }} />
                            <div style={{ padding: 4 }}>
                                <div style={{ height: 4, width: '70%', background: '#e5e7eb', borderRadius: 2, marginBottom: 3 }} />
                                <div style={{ height: 4, width: '40%', background: '#d1d5db', borderRadius: 2 }} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {/* Bottom Nav Bar */}
            <div style={getBarStyle()}>
                {tabs.map((tab, i) => renderTab(tab, i))}
            </div>
        </div>
    );
};

// =============================================
// HOME THEME PREVIEW (Phone Mockup)
// =============================================
const HomeThemePreview = ({ themeId }) => {
    const theme = HOME_THEME_OPTIONS.find(t => t.value === themeId) || HOME_THEME_OPTIONS[0];
    const [c1, c2] = theme.preview || ['#f3f4f6', '#e5e7eb'];
    
    // Animation keyframes for particles
    const particleStyle = `
        @keyframes float-up { 0% { transform: translateY(0) rotate(0deg); opacity: 1; } 100% { transform: translateY(-120px) rotate(360deg); opacity: 0; } }
        @keyframes snow-fall { 0% { transform: translateY(-10px) translateX(0); opacity: 0; } 10% { opacity: 1; } 100% { transform: translateY(300px) translateX(20px); opacity: 0; } }
        @keyframes pulse-glow { 0%, 100% { box-shadow: 0 0 8px ${c1}88; } 50% { box-shadow: 0 0 20px ${c1}cc; } }
        @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
    `;

    const getParticles = () => {
        const particles = [];
        const count = 6;
        if (themeId === 'none') return null;
        
        for (let i = 0; i < count; i++) {
            const left = `${10 + (i * 14)}%`;
            const delay = `${i * 0.5}s`;
            const size = 4 + (i % 3) * 2;
            let char = '✦';
            if (['christmas', 'winter-wonderland'].includes(themeId)) char = '❄';
            else if (themeId === 'diwali') char = '✨';
            else if (['holi'].includes(themeId)) char = '⬤';
            else if (themeId === 'spring-blossom' || themeId === 'cherry-festival') char = '🌸';
            else if (themeId === 'valentines') char = '♥';
            else if (['autumn-harvest'].includes(themeId)) char = '🍂';
            else if (themeId === 'monsoon') char = '💧';
            else if (themeId === 'summer-vibes' || themeId === 'summer-tropical') char = '☀';
            else if (['halloween'].includes(themeId)) char = '🦇';
            else if (['new-year'].includes(themeId)) char = '✨';
            
            particles.push(
                <span key={i} style={{ position: 'absolute', left, top: '10%', fontSize: size, animation: `snow-fall 3s ease-in ${delay} infinite`, opacity: 0.7 }}>
                    {char}
                </span>
            );
        }
        return particles;
    };

    return (
        <div className="relative mx-auto" style={{ width: 220, height: 420, borderRadius: 30, border: '8px solid #1f2937', backgroundColor: '#fff', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <style>{particleStyle}</style>
            {/* Status Bar */}
            <div style={{ height: 24, background: `linear-gradient(135deg, ${c1}, ${c2})`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 12px', fontSize: 8, color: '#fff', fontWeight: 700 }}>
                <span>9:41</span>
                <div style={{ display: 'flex', gap: 3 }}>
                    <span>📶</span><span>🔋</span>
                </div>
            </div>
            {/* Header */}
            <div style={{ height: 44, background: `linear-gradient(135deg, ${c1}dd, ${c2}dd)`, display: 'flex', alignItems: 'center', padding: '0 10px', gap: 6, position: 'relative', overflow: 'hidden' }}>
                <div style={{ width: 24, height: 24, borderRadius: 12, background: 'rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>🏪</div>
                <div style={{ flex: 1, height: 22, borderRadius: 11, background: 'rgba(255,255,255,0.35)', display: 'flex', alignItems: 'center', paddingLeft: 8, fontSize: 7, color: 'rgba(255,255,255,0.8)' }}>🔍 Search products...</div>
                <div style={{ width: 4, height: 4, borderRadius: 2, background: '#fff', position: 'absolute', top: 8, right: 12, animation: 'pulse-glow 2s infinite' }} />
            </div>
            {/* Banner */}
            <div style={{ height: 100, background: `linear-gradient(135deg, ${c1}, ${c2})`, position: 'relative', overflow: 'hidden' }}>
                {getParticles()}
                <div style={{ position: 'absolute', bottom: 8, left: 10, right: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <div>
                        <div style={{ fontSize: 9, fontWeight: 800, color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>{theme.label}</div>
                        <div style={{ fontSize: 6, color: 'rgba(255,255,255,0.8)' }}>Special Offers Inside</div>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.3)', borderRadius: 8, padding: '2px 6px', fontSize: 6, color: '#fff', fontWeight: 700, backdropFilter: 'blur(4px)' }}>Explore →</div>
                </div>
                <div style={{ position: 'absolute', top: 6, right: 8, fontSize: 22 }}>{theme.icon}</div>
            </div>
            {/* Categories */}
            <div style={{ padding: '6px 8px' }}>
                <div style={{ fontSize: 7, fontWeight: 700, color: '#374151', marginBottom: 4 }}>Shop by Category</div>
                <div style={{ display: 'flex', gap: 6 }}>
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} style={{ textAlign: 'center' }}>
                            <div style={{ width: 30, height: 30, borderRadius: 15, background: `linear-gradient(135deg, ${c1}40, ${c2}40)`, border: `1.5px solid ${c1}60`, animation: 'pulse-glow 3s infinite', animationDelay: `${i * 0.3}s` }} />
                            <div style={{ fontSize: 5, color: '#6b7280', marginTop: 2 }}>Cat {i}</div>
                        </div>
                    ))}
                </div>
            </div>
            {/* Product Section */}
            <div style={{ padding: '4px 8px' }}>
                <div style={{ fontSize: 7, fontWeight: 700, color: '#374151', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
                    <div style={{ width: 2, height: 8, borderRadius: 1, background: c1 }} />
                    Featured Products
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                    {[1, 2].map(i => (
                        <div key={i} style={{ flex: 1, borderRadius: 6, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                            <div style={{ height: 50, background: `linear-gradient(135deg, ${c1}20, ${c2}20)`, position: 'relative', overflow: 'hidden' }}>
                                <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)`, animation: 'shimmer 2s infinite' }} />
                            </div>
                            <div style={{ padding: '3px 4px' }}>
                                <div style={{ height: 4, width: '80%', background: '#e5e7eb', borderRadius: 2, marginBottom: 2 }} />
                                <div style={{ height: 4, width: '50%', background: c1, borderRadius: 2, opacity: 0.7 }} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {/* Bottom Section */}
            <div style={{ padding: '6px 8px' }}>
                <div style={{ display: 'flex', gap: 4 }}>
                    {[1, 2].map(i => (
                        <div key={i} style={{ flex: 1, borderRadius: 6, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                            <div style={{ height: 50, background: `linear-gradient(135deg, ${c2}20, ${c1}20)`, position: 'relative', overflow: 'hidden' }}>
                                <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)`, animation: 'shimmer 2.5s infinite' }} />
                            </div>
                            <div style={{ padding: '3px 4px' }}>
                                <div style={{ height: 4, width: '70%', background: '#e5e7eb', borderRadius: 2, marginBottom: 2 }} />
                                <div style={{ height: 4, width: '40%', background: c2, borderRadius: 2, opacity: 0.7 }} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {/* Bottom Nav */}
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 32, background: '#fff', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-around', alignItems: 'center', fontSize: 10 }}>
                <span style={{ color: c1 }}>🏠</span>
                <span style={{ color: '#9ca3af' }}>💖</span>
                <span style={{ color: '#9ca3af' }}>🛒</span>
                <span style={{ color: '#9ca3af' }}>📦</span>
                <span style={{ color: '#9ca3af' }}>👤</span>
            </div>
        </div>
    );
};

export default CategoryCustomization;
